import { bridge } from '../bridge'
import { useAppStore, attentionBadgeTotal, type HouseholdAttention } from '../store/app'
import { markOutdated } from '../store/outdated'
import type {
  HistoryItem,
  ThreadSummary,
  PendingInvite,
  ScheduledTrigger,
  ScheduledTriggerRun,
} from '../store/types'
import { probeEnrollment } from './client'
import { getClientHeader } from './clientHeader'
import { serverBaseURL } from './config'
import { keychain } from './keychain'
import { signOutRemotely } from './logout'
import { parseServerTime } from '../util/serverTime'
import {
  approvalPendingSpeech,
  type ApprovalAudience,
} from '../screens/agents/voiceSession'

/**
 * Say out loud that an action is waiting on approval, if a voice session is
 * open — otherwise a session goes silent on someone who just asked for
 * something, which is the failure the relay's puck-side line exists to prevent.
 *
 * Spoken from the message router because this is where the two frames arrive
 * and where their audiences are distinguishable: `permission_request` means YOU
 * are being asked, `permission_pending` means someone else is. The store
 * already fires haptics from here for the same reason.
 *
 * `agentInstanceID` scopes the line to the agent the session is pinned to.
 * `permission_pending` carries no agent id, so it passes undefined and speaks
 * whenever any session is open — the line names nothing, so being a beat
 * out of context costs far less than silence does.
 */
function speakApprovalLine(audience: ApprovalAudience, agentInstanceID?: string) {
  const active = useAppStore.getState().voiceSessionAgentID
  if (!active) return
  if (agentInstanceID && agentInstanceID !== active) return
  bridge.call('voice.speak', { text: approvalPendingSpeech(audience) }).catch(() => {})
}

// Single-connection WebSocket client modeled on HearthService's
// connect/disconnect/reconnect loop. Connects on registration + every time
// the app returns to the foreground; disconnects when backgrounded so the
// server falls back to APNs for pending requests.

let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
let wantConnected = false
// Guards against re-entry into connect() while an earlier call is still
// awaiting keychain reads. Without this, React StrictMode's double-mount
// in dev (and any other burst of startWS calls) opens two WebSockets for
// the same io_device_id, which the server's single-connection-per-device
// policy resolves by closing the older one — the client then sees the
// close as "connection lost" and reconnects, kicking off an infinite
// connect/disconnect cycle.
let connecting = false
// Active per-instance subscriptions the client has declared. Mounted
// agent-detail screens add/remove themselves; on every /ws open we
// re-fire subscribe_agent for each so the server's in-memory set is
// rebuilt after a reconnect.
const activeAgentSubscriptions = new Set<string>()

function scheduleReconnect() {
  if (!wantConnected) return
  if (reconnectTimer) return
  const delay = Math.min(30_000, 500 * 2 ** reconnectAttempts)
  reconnectAttempts++
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    void connect()
  }, delay)
}

// ── Application-level heartbeat ──────────────────────────────────────────
// The browser WebSocket API hides protocol ping/pong from JS, so a half-open
// socket — TCP silently dead (e.g. after a cellular tower handoff or radio
// sleep) yet still reporting readyState===OPEN — swallows every send with no
// error. We can't detect that passively, and our only other recovery is the
// app.foreground reconnect, which never fires while the app stays foregrounded.
// So we ping the server on an interval and force a reconnect when the pong
// doesn't come back. This is what keeps a foregrounded session from silently
// going deaf on a flaky connection. Runs only while connected (started in
// onopen, stopped in onclose / stopWS); backgrounding tears the socket down.
const HEARTBEAT_INTERVAL_MS = 25_000
const HEARTBEAT_TIMEOUT_MS = 10_000
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

function startHeartbeat() {
  stopHeartbeat()
  // Per-socket closure state, reset on every (re)connect:
  //  - sawPong gates the watchdog. We only reconnect on a MISS after at least
  //    one successful pong on THIS socket — proof the server answers app-pings
  //    and the socket was healthy, so a later miss is a genuine half-open. If
  //    the very first ping goes unanswered (a server too old to handle `ping`),
  //    we quietly stop the heartbeat instead of reconnect-storming against it.
  //  - inFlight keeps one ping outstanding at a time; the pending one IS the
  //    probe, so we don't stack them.
  let sawPong = false
  let inFlight = false
  heartbeatTimer = setInterval(() => {
    if (inFlight) return
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    inFlight = true
    const probed = socket
    wsRequest('ping', {}, 'pong', { kind: 'ws', action: 'ping' }, { timeoutMs: HEARTBEAT_TIMEOUT_MS })
      .then(() => {
        sawPong = true
        inFlight = false
      })
      .catch(() => {
        inFlight = false
        // Ignore if a reconnect already swapped the socket or we're stopping.
        if (socket !== probed || !wantConnected) return
        if (!sawPong) {
          // Server never answered a ping on this socket — assume it predates the
          // `ping` handler. Stop pinging rather than reconnect every cycle.
          stopHeartbeat()
          return
        }
        console.warn('ws: heartbeat pong missed — reconnecting (half-open socket)')
        reconnectWS()
      })
  }, HEARTBEAT_INTERVAL_MS)
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

// Count of consecutive connect attempts that closed WITHOUT ever reaching
// onopen. A dropped-mid-session socket resets this (onopen ran); a socket
// the server keeps refusing accumulates it. Reset on any successful open.
let wsConnectFailures = 0
// After this many refused-in-a-row connects (~3.5s of backoff) we stop
// assuming "network blip" and check whether we've actually been unenrolled.
// This is the fallback for a server that still rejects with a bare 403 (old
// build) — a current server sends WS_CLOSE_DEVICE_REVOKED instead, which we
// act on immediately without waiting out this counter.
const WS_REFUSAL_PROBE_THRESHOLD = 3
// Server close code for "your device credentials are rejected" (revoked /
// unenrolled). Mirrors WSCloseDeviceUnauthorized in the relay. The browser
// WebSocket API can't read the pre-upgrade 403, but it CAN read this code.
const WS_CLOSE_DEVICE_REVOKED = 4403
// Single-flight guard: a burst of failures triggers at most one probe.
let verifyingEnrollment = false

// The browser WebSocket API never exposes the handshake's HTTP status, so a
// /ws rejected with 403 (device revoked) reaches us only as close code 1006 —
// identical to a network drop. Left alone, a remotely-unenrolled device sits
// in the app forever showing "disconnected" and silently reconnecting. When
// the socket has been refused several times running, confirm with an HTTP
// probe (whose status we CAN read) and, only if the server rejects our
// credentials, sign out locally so the user lands on the login screen with a
// clear reason — instead of a limbo "disconnected" app. A genuine
// server/host outage returns 'inconclusive' and we keep retrying, unchanged.
async function verifyEnrollmentOrSignOut(): Promise<void> {
  if (verifyingEnrollment) return
  if (!useAppStore.getState().isRegistered) return
  verifyingEnrollment = true
  try {
    const status = await probeEnrollment()
    if (status === 'revoked') {
      wantConnected = false
      wsConnectFailures = 0
      await signOutRemotely()
    }
  } finally {
    verifyingEnrollment = false
  }
}

async function connect() {
  if (connecting) return
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }
  connecting = true
  try {
    const ioDeviceID = await keychain.ioDeviceID()
    const secret = await keychain.ioDeviceSecret()
    if (!ioDeviceID || !secret) return
    const base = await serverBaseURL()
    // Empty base = same-origin (dev proxy): derive the ws URL from
    // window.location so the browser connects to Vite which ws-proxies to
    // permit-cloud. Non-empty = absolute; swap http(s):// → ws(s)://.
    const wsBase =
      base === ''
        ? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`
        : base.replace(/^http/, 'ws')
    const ch = await getClientHeader()
    const url =
      `${wsBase}/ws` +
      `?io_device_id=${encodeURIComponent(ioDeviceID)}` +
      `&secret=${encodeURIComponent(secret)}` +
      `&client_kind=${encodeURIComponent(ch.kind)}` +
      `&client_version=${encodeURIComponent(ch.version)}`

    useAppStore.getState().setConnectionStatus('connecting')
    const ws = new WebSocket(url)
    socket = ws
    // Per-socket flag so onclose can tell "refused before opening" (a
    // possible auth rejection) from "dropped after a good session" (a
    // network blip). Closes over this one connect() invocation.
    let opened = false

    ws.onopen = () => {
      opened = true
      reconnectAttempts = 0
      wsConnectFailures = 0
      useAppStore.getState().setConnectionStatus('connected')
      // Watchdog the freshly-opened socket for silent (half-open) death.
      startHeartbeat()
      // Server's subscriber map is in-memory and clears on disconnect, so
      // every reconnect must re-declare the active subscriptions AND re-request
      // each agent's transcript. Unlike permission history there's no `since`
      // cursor for transcripts, so we replay the whole tail; the per-agent
      // store bucket dedups by content id, making the overlap harmless. Without
      // the transcript re-request, entries emitted during the disconnect window
      // are lost until the user navigates away and back (W6).
      for (const id of activeAgentSubscriptions) {
        ws.send(JSON.stringify({ type: 'subscribe_agent', ai_agent_instance_id: id }))
        ws.send(JSON.stringify({ type: 'request_transcript_history', ai_agent_instance_id: id }))
      }
      // Gap-fill: for every position that had history loaded in this session,
      // request only rows newer than the most recent we've already seen.
      // This covers the bg→fg reconnect window where live events were missed.
      const { historyLoadedFor, positionHistory } = useAppStore.getState()
      for (const positionID of Object.keys(historyLoadedFor)) {
        const bucket = positionHistory[positionID] ?? []
        const latest = bucket.reduce<number>((mx, h) => Math.max(mx, h.occurred_at), 0)
        const since = latest > 0 ? new Date(latest).toISOString() : undefined
        sendRequestAgentHistory(positionID, since)
      }
      // Durable initiator notices (X2): the socket was down, so any live
      // permission_pending frames were missed — re-seed the banner from the
      // open-requests pull surface so a returning user still sees the requests
      // they started that are still pending.
      void rehydrateInitiatorNotices()
      // Per-household attention counts for the switcher chips (cross-household
      // "B wants you" signal). Cheap; refreshed on every connect.
      void fetchAttention()
      // Unread followed threads (and rooms): they mark the chat-room list and
      // the thread summary rows, not only the inbox, so keep them current.
      void fetchAttentionRooms()
      // Run the deferred pending-approvals refresh if a foreground (cold
      // launch OR bg→fg) is waiting on this connect. Transient reconnects
      // (no fg event in between) don't set the flag. The refresh settles
      // the list and badge; it never opens an approval.
      if (awaitingForegroundRefresh) {
        awaitingForegroundRefresh = false
        void refreshPendingApprovals()
      }
      // Cold launch from a push in another household: the tap fired
      // before this socket existed, so the switch was parked. Send it
      // now, before anything downstream tries to resolve a destination
      // id against the wrong household's lists. Re-check current org —
      // the connect itself may have already landed us there.
      if (pendingPushOrgSwitchID) {
        const target = pendingPushOrgSwitchID
        pendingPushOrgSwitchID = null
        if (target !== useAppStore.getState().organizationID) {
          sendSetCurrentOrganization(target)
        }
      }
    }
    ws.onclose = (ev) => {
      // If this isn't the current socket, stopWS() already replaced it with
      // a newer connection (or null). Don't clobber the live status or
      // schedule a redundant reconnect for a socket we intentionally retired.
      if (socket !== ws) return
      socket = null
      stopHeartbeat()
      useAppStore.getState().setConnectionStatus('disconnected')
      // Custom close code 4426 = server says this client is below the
      // version policy's `min`. Surface to the outdated modal and stop
      // reconnecting — there is nothing to retry.
      if (ev.code === 4426) {
        markOutdated({ minVersion: '', yourVersion: ch.version })
        wantConnected = false
        return
      }
      // Custom close code 4403 = the server explicitly rejected our device
      // credentials (revoked / unenrolled). This is the deterministic,
      // immediate path: unlike a bare 403 (invisible to a browser WebSocket),
      // this code is readable, so we don't have to wait out the refusal
      // counter. Confirm with the probe and sign out. We still let the normal
      // reconnect proceed — if the probe comes back inconclusive (server
      // flaky, not an auth verdict) we keep retrying; a confirmed revocation
      // cancels the reconnect via stopWS() inside signOutRemotely.
      if (ev.code === WS_CLOSE_DEVICE_REVOKED) {
        void verifyEnrollmentOrSignOut()
        scheduleReconnect()
        return
      }
      // A socket that never opened was refused, not dropped. Enough of those
      // in a row and we probe for a remote unenroll (see
      // verifyEnrollmentOrSignOut). Only while we still want to be connected —
      // an intentional stop (background/logout) isn't a refusal. onopen resets
      // the counter, so a healthy session that later drops never trips this.
      if (wantConnected && !opened) {
        wsConnectFailures++
        if (wsConnectFailures >= WS_REFUSAL_PROBE_THRESHOLD) {
          void verifyEnrollmentOrSignOut()
        }
      }
      scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose will fire right after; rely on that for reconnect.
    }
    ws.onmessage = (ev) => {
      try {
        const raw = typeof ev.data === 'string' ? ev.data : ''
        const msg = JSON.parse(raw)
        if (msg?.type === 'client_outdated') {
          markOutdated({
            minVersion: msg.min_version ?? '',
            yourVersion: msg.your_version ?? ch.version,
            updateURL: msg.update_url,
          })
          wantConnected = false
          return
        }
        handleMessage(msg)
      } catch (err) {
        console.error('ws: bad message', err)
      }
    }
  } finally {
    connecting = false
  }
}

export function startWS() {
  wantConnected = true
  void connect()
}

/// Force a clean disconnect + reconnect. The server's scoped-list pushes
/// (agents, positions, rules, orgs, invites) all key off the device's
/// current_organization_id and are only replayed IN FULL at connect time.
/// A mid-session `set_current_organization` is supposed to re-push them,
/// but it rides sendMessage, which silently drops if the socket isn't
/// OPEN — so a UI transition that happens to coincide with any socket
/// churn loses the lists. Joining a household changes the entire scope at
/// once, so rather than depend on that fragile fire-and-forget, we do what
/// a manual browser refresh does and reconnect. onclose is guarded against
/// the retired socket, so this is safe.
export function reconnectWS() {
  stopWS()
  startWS()
}

export function stopWS() {
  wantConnected = false
  // Abandon any household switch a push tap parked for the next open.
  // It is scoped to the tap that requested it, not to the device: an
  // explicit teardown (backgrounding, sign-out, a join-household
  // reconnect that sets its own household) means that tap's moment has
  // passed. Without this the target survives indefinitely and fires on
  // some later reconnect, yanking the user out of whichever household
  // they picked in the meantime.
  pendingPushOrgSwitchID = null
  stopHeartbeat()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  if (socket) {
    socket.close()
    socket = null
  }
  useAppStore.getState().setConnectionStatus('disconnected')
}

// Action declares (resource, action) for a call. The shape rides on
// every authenticated WS message so future IAM can enforce policy
// without touching call sites. Spec:
// hearth-cmd/docs/forward-compat-resource-scope-tuple.md.
export type Action = { kind: string; id?: string; action: string }

function ctxFrom(a: Action) {
  return { resource: { kind: a.kind, id: a.id ?? '' }, action: a.action }
}

// Returns whether the frame was actually handed to an OPEN socket. Callers that
// need delivery certainty (wsRequest) act on `false` immediately rather than
// waiting for a response that can never arrive; fire-and-forget callers ignore
// it. This is the guard behind the "message silently vanished" class of bug:
// a closed/half-open socket must never look like a successful send.
export function sendMessage(msg: Record<string, unknown>, action: Action): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    console.warn('ws: send attempted while not connected', msg)
    return false
  }
  socket.send(JSON.stringify({ ...msg, ctx: ctxFrom(action) }))
  return true
}

// Sequential request/response over the device /ws: phone sends a
// ws_request-style message (type + data), server responds with a discrete
// typed message (e.g. `harnesses`, `organization_position_created`). Matches
// by response type in FIFO order — good enough since the caller serializes
// requests per response type (wizard steps run one at a time).
const pendingRequests = new Map<string, Array<(msg: any) => void>>()

// pendingByID: primary id-keyed match. The server echoes
// client_request_id from the request into the response so we resolve
// against THIS request's resolver, not the next-in-line by type.
//
// Each entry also records the response TYPE the request expects, because the
// server's echo is positional, not semantic: it tags the FIRST frame written
// to this connection while it's handling the request (deviceConn.WriteJSON),
// and a frame written concurrently by something else — a broadcast of someone
// else's chat message, a background unread-counts push — can land first and
// take the tag. Resolving on id alone then handed the request the wrong frame:
// opening a room resolved list_chat_messages with a chat_unread_counts push,
// saw no messages, and rendered the room BLANK, while the real (now untagged)
// response found no taker. So an id match only counts when the frame is the
// expected type (or a generic `error`); otherwise it's ignored for matching
// and the real response still resolves by type below.
const pendingByID = new Map<string, { resolve: (msg: any) => void; responseType: string }>()

// oneshotHandlers: ad-hoc one-shot listeners registered by UI components
// for unsolicited server events (e.g. connection_authorized). Fired
// once then auto-removed. Use subscribeOnce() to register.
const oneshotHandlers = new Map<string, Array<(msg: any) => void>>()

export function subscribeOnce<T = any>(type: string, cb: (msg: T) => void): () => void {
  const handlers = oneshotHandlers.get(type) ?? []
  handlers.push(cb as (msg: any) => void)
  oneshotHandlers.set(type, handlers)
  return () => {
    const hs = oneshotHandlers.get(type)
    if (!hs) return
    const idx = hs.indexOf(cb as (msg: any) => void)
    if (idx >= 0) hs.splice(idx, 1)
  }
}

// Tiny UUID v4 helper. crypto.randomUUID is available in WKWebView
// (iOS 15.4+) and modern browsers; fall back for older environments.
function makeRequestID(): string {
  const c = (globalThis as any).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Math.random fallback — sufficient for matching, not cryptographic.
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2)
  )
}

// Gated household-CRUD msg_types the relay may route to the household owner for
// approval (crud_authz.go householdCRUDGates). When a non-owner initiates one,
// the request blocks server-side until the owner responds, so the client must
// wait the long ask window rather than the 15s default — otherwise it times out
// while the relay still executes the mutation on approval. (For the owner these
// proceed immediately, so the long cap is only ever reached mid-approval.)
const GATED_CRUD_SEND_TYPES = new Set<string>([
  'create_ai_agent_instance', 'create_temp_agent_instance', 'update_ai_agent_instance',
  'retire_ai_agent_instance', 'wake_ai_agent_instance', 'sleep_ai_agent_instance',
  'create_organization_position', 'update_organization_position', 'eliminate_organization_position',
  'create_working_directory', 'find_or_create_working_directory', 'update_working_directory', 'abandon_working_directory',
  'create_ai_brain_model', 'update_ai_brain_model', 'archive_ai_brain_model',
  'create_agent_job_description', 'update_agent_job_description', 'archive_agent_job_description',
  'update_organization',
  'add_organization_member', 'remove_organization_member',
])

export function wsRequest<T = any>(
  sendType: string,
  data: Record<string, unknown> | undefined,
  responseType: string,
  action: Action,
  opts: { timeoutMs?: number } = {},
): Promise<T> {
  const timeoutMs =
    opts.timeoutMs ?? (GATED_CRUD_SEND_TYPES.has(sendType) ? 12 * 60_000 : 15_000)
  const requestID = makeRequestID()
  return new Promise<T>((resolve, reject) => {
    let done = false
    const resolver = (msg: any) => {
      if (done) return
      done = true
      clearTimeout(timer)
      pendingByID.delete(requestID)
      // Also clean up the legacy type-keyed queue entry if it's still
      // there. Resolvers are referenced from both maps until either
      // path fires.
      removeFromTypeQueue(responseType, resolver)
      resolve(msg as T)
    }
    pendingByID.set(requestID, { resolve: resolver, responseType })
    // Legacy FIFO fallback — preserved for older server builds that
    // don't echo client_request_id, and for any handler that writes
    // a non-object JSON response (interceptor falls through in that
    // case). Once the deployed server reliably tags, this branch
    // becomes dead code and can be removed.
    const queue = pendingRequests.get(responseType) ?? []
    queue.push(resolver)
    pendingRequests.set(responseType, queue)

    const timer = setTimeout(() => {
      if (done) return
      done = true
      pendingByID.delete(requestID)
      removeFromTypeQueue(responseType, resolver)
      reject(new Error(`wsRequest ${sendType} → ${responseType} timed out`))
    }, timeoutMs)
    const delivered = sendMessage(
      { type: sendType, data: data ?? {}, client_request_id: requestID },
      action,
    )
    // The socket wasn't OPEN, so no response can ever come back. Reject now
    // instead of stalling the full timeout — the caller gets an immediate,
    // actionable failure (offline/reconnecting) rather than a 15s hang that
    // then looks like a silent drop.
    if (!delivered && !done) {
      done = true
      clearTimeout(timer)
      pendingByID.delete(requestID)
      removeFromTypeQueue(responseType, resolver)
      reject(new Error(`wsRequest ${sendType}: socket not connected`))
    }
  })
}

function removeFromTypeQueue(
  responseType: string,
  resolver: (msg: any) => void,
): void {
  const q = pendingRequests.get(responseType)
  if (!q) return
  const idx = q.indexOf(resolver)
  if (idx < 0) return
  q.splice(idx, 1)
  if (q.length === 0) pendingRequests.delete(responseType)
}

// Returns true if the message was consumed by a pending wsRequest resolver
// (either id-keyed or FIFO). The caller uses this to suppress the
// "unhandled message type" log for expected wsRequest responses.
function deliverPendingRequest(msg: any): boolean {
  // Id-keyed path: precise 1:1 match between server response and
  // the wsRequest that originated it. Defended against FIFO mismatch
  // when two same-type requests are in flight.
  const reqID = msg?.client_request_id
  if (typeof reqID === 'string' && reqID) {
    const entry = pendingByID.get(reqID)
    // Only the expected response type (or a generic error) answers this
    // request. Anything else that carries its id took the tag by accident —
    // see pendingByID — so leave the request pending for its real response.
    if (entry && (msg?.type === entry.responseType || msg?.type === 'error')) {
      pendingByID.delete(reqID)
      entry.resolve(msg)
      return true
    }
  }
  // Legacy FIFO-by-type fallback. Server-pushed frames (broadcasts,
  // replays) reach this branch since they carry no client_request_id;
  // legitimately-async listeners are intentionally allowed to consume
  // them. Same path used when talking to a server build that predates
  // the echo support.
  const t = msg?.type
  if (typeof t !== 'string') return false
  const queue = pendingRequests.get(t)
  if (!queue || queue.length === 0) return false
  const next = queue.shift()!
  // Resolver may also be in the id-keyed map — clear it there so
  // a late-arriving id-matched frame doesn't double-fire.
  for (const [id, entry] of pendingByID) {
    if (entry.resolve === next) {
      pendingByID.delete(id)
      break
    }
  }
  next(msg)
  return true
}

// Register lifecycle + subscription listeners once. The bridge's event
// emitter dedupes per-handler so this is safe to call at module load.
bridge.on('app.foreground', () => {
  // Force a fresh socket on resume rather than trusting the one we have.
  // The OS can suspend the WebView and later resume it holding a WebSocket
  // that still reports readyState===OPEN while its TCP connection is
  // silently dead. connect()'s early-return trusts that "OPEN" socket, so
  // no new socket opens and none of the connect-time pushes (hosts_list,
  // ai_agent_instances_list, …) are replayed — leaving host online/offline
  // (and every other server-scoped list) frozen at its last value until a
  // cold launch. That's the "host shows offline until I hard-close and
  // reopen" bug. We can't detect a half-open socket passively (the browser
  // never surfaces the server's protocol pongs to JS), and app.background's
  // stopWS() can't be relied on either — if the runloop was frozen before
  // it drained, the socket survives. So we always reconnect. In the normal
  // case app.background already nulled the socket, so this is identical to
  // startWS(); its only extra cost is a reconnect when a genuinely-healthy
  // socket survived a transient active/inactive flicker (e.g. pulling down
  // Control Center), which is occasional, user-driven, and idempotent.
  if (useAppStore.getState().isRegistered) reconnectWS()
  // Reconcile delivered notifications against whatever pending set we
  // already have in memory. Covers the "no pending requests → store
  // doesn't change → subscriber below never fires" case where a stale
  // banner would otherwise sit forever.
  //
  // Not authoritative: this runs before reconnectWS + the refresh below
  // have re-established what's actually pending, and on cold launch the
  // in-memory set is empty. Prune banners on it, but don't let it move the
  // badge — refreshPendingApprovals does that once it has the truth.
  const activeRequestIDs = useAppStore
    .getState()
    .pendingRequests.map((r) => r.id)
  void bridge
    .call('notifications.reconcile', { activeRequestIDs, authoritative: false })
    .catch(() => {})
  triggerForegroundRefresh()
})

// triggerForegroundRefresh: ensure refreshPendingApprovals runs
// exactly once after the WS is OPEN. If the socket is already OPEN,
// fire immediately. Otherwise set the awaiting flag so ws.onopen
// fires it after the handshake completes. Idempotent — multiple
// calls before onopen coalesce to one run.
function triggerForegroundRefresh(): void {
  if (socket && socket.readyState === WebSocket.OPEN) {
    awaitingForegroundRefresh = false
    void refreshPendingApprovals()
    void fetchAttention()
    void fetchAttentionRooms()
    return
  }
  awaitingForegroundRefresh = true
}

// Refresh the unread-threads list soon after the server says counts changed (a
// reply in a thread you follow, or you read something on another device). The
// list marks which rooms and which threads have new replies (HomeScreen room
// rows, ThreadSummaryRow). Debounced: a burst of replies is one refresh.
let attentionRoomsRefreshTimer: ReturnType<typeof setTimeout> | null = null
function scheduleAttentionRoomsRefresh(): void {
  if (attentionRoomsRefreshTimer) clearTimeout(attentionRoomsRefreshTimer)
  attentionRoomsRefreshTimer = setTimeout(() => {
    attentionRoomsRefreshTimer = null
    void fetchAttentionRooms()
  }, 400)
}

// Household a pending push tap wants us on, held until the socket is
// OPEN. Same deferral shape as awaitingForegroundRefresh and for the
// same reason: a cold launch from a notification fires push.tapped
// before the handshake completes, and sendSetCurrentOrganization rides
// sendMessage, which silently drops when readyState !== OPEN. Dropping
// it is exactly the bug this exists to fix, so it can't be
// fire-and-forget.
let pendingPushOrgSwitchID: string | null = null

// switchOrganizationForPush re-points the device at the household a
// tapped push belongs to. No-op when we're already there — the server
// would handle a redundant switch fine, but it re-pushes every scoped
// list, which is a visible flicker on the common same-household tap.
//
// Deliberately does NOT block the caller's navigation. Chat's read and
// send handlers authorize on household *membership*, not on the
// device's selected household, so the room screen works the moment it
// mounts and simply gets better (room metadata, file uploads, the
// screen behind it) once the switch lands. The agent path can't
// navigate early — it needs a roster only the switch will deliver — but
// it already defers through pendingPushPositionID, whose subscriber
// retries on the next agentInstances change. That change is precisely
// what the switch triggers.
function switchOrganizationForPush(organizationID: string | undefined): void {
  if (!organizationID) return
  if (organizationID === useAppStore.getState().organizationID) return
  if (socket && socket.readyState === WebSocket.OPEN) {
    pendingPushOrgSwitchID = null
    sendSetCurrentOrganization(organizationID)
    return
  }
  pendingPushOrgSwitchID = organizationID
}
bridge.on('app.background', () => {
  // Fully stop (wantConnected=false + clear timer) so a backgrounded tab
  // does not keep reconnecting after being displaced by another tab that
  // shares the same io_device_id. app.foreground's startWS() re-enables
  // when the tab comes back. Matches HearthService.appDidResignActive().
  stopWS()
})

// Set by app.foreground; consumed by ws.onopen so the refresh fires
// after the socket is actually OPEN. Without this, the wsRequest
// fired before WS handshake completes hits sendMessage's
// readyState !== OPEN guard and gets silently dropped.
let awaitingForegroundRefresh = false

// refreshPendingApprovals fetches the canonical pending-approvals list
// (cross-org coverage included), merges it into the store, prunes what the
// server no longer reports, and settles the badge. It deliberately opens
// nothing: coming back to the app (or refreshing the browser) never throws an
// approval at you. Waiting approvals sit in the inbox behind the bell, where
// you answer them in the list. One opens full-screen only on a clear signal:
// tapping its push, or it arriving while you're looking at that agent or a chat
// room. (This used to pop the newest newcomer, which showed one of several and
// left the rest behind: neither a full view nor none.)
async function refreshPendingApprovals(): Promise<void> {
  let r:
    | {
        entries?: PendingApprovalEntry[]
        unread_room_count?: number
        unread_mention_count?: number
        unread_thread_count?: number
        pending_suggestion_count?: number
      }
    | undefined
  try {
    r = await fetchPendingApprovals()
  } catch {
    return
  }
  const entries = r?.entries
  // Prefer the new room-count; fall back to the mention field only if a
  // mid-deploy server hasn't started sending unread_room_count yet.
  const unreadRooms = r?.unread_room_count ?? r?.unread_mention_count
  if (!entries) return

  const s = useAppStore.getState()

  // Merge into the local store so banner / sheet see them.
  const existing = new Set(s.pendingRequests.map((p) => p.id))
  for (const e of entries) {
    if (!existing.has(e.request_id)) {
      s.enqueueRequest(normalizePendingApprovalEntry(e))
    }
  }

  // ...and prune what the server no longer reports.
  //
  // permission_resolved / cancel_request frames are the only other removal
  // path, and they don't arrive while the socket is down — so an ask answered
  // on another device stayed in this device's list until the app was
  // force-quit. That showed up as an approval queue listing rows the web app
  // had already resolved, and as a badge that wouldn't come down: the WS
  // reconnect replays the still-live asks, which mutates pendingRequests,
  // which fires the reconcile subscriber, which re-asserts the stale count
  // over the correct one computed from `entries` just below.
  //
  // Merge-then-prune rather than wholesale replace: an entry already present
  // locally may carry richer fields from the live permission_request frame
  // than the inbox row does, and there's no reason to throw those away.
  //
  // Note this is approver-scoped, so an ask this device is merely OBSERVING
  // (a permission_request frame reaches every device subscribed to the agent,
  // approver or not) gets pruned on the next refresh. That's the intended
  // reading of the pending-approvals surface — it is what you can decide.
  s.retainPendingRequests(entries.map((e) => e.request_id))

  // The chat half of the badge rides this same canonical refresh, so the two
  // halves are always settled from one consistent server view.
  if (typeof unreadRooms === 'number') s.setUnreadRoomCount(unreadRooms)
  if (typeof r?.unread_thread_count === 'number') s.setUnreadThreadCount(r.unread_thread_count)
  if (typeof r?.pending_suggestion_count === 'number') {
    s.setPendingSuggestionCount(r.pending_suggestion_count)
  }

  // Settle the badge on what the SERVER says is waiting on this human, not
  // on the local pending set, and do it unconditionally — including when
  // nothing changed, which is exactly the "you came back and two are still
  // waiting" case the store subscriber below can't fire for.
  //
  // entries is the right set for both jobs reconcile does. It's
  // approver-scoped, so it matches the count the server puts on pushes;
  // pendingRequests is not, because a permission_request frame reaches every
  // device subscribed to the agent, approver or not (see fanout's
  // "informational broadcast"), and a non-approver watching an agent would
  // otherwise badge for asks that were never theirs to answer. It's also
  // correct for banner pruning, since pushes only ever go to approvers, so
  // no banner exists outside this set.
  void bridge
    .call('notifications.reconcile', {
      activeRequestIDs: entries.map((e) => e.request_id),
      authoritative: true,
      // Asks from the canonical list, unread-rooms + invites from the store —
      // the same three inputs the server sums for the app-icon badge, so the
      // in-app badge can't drift from it. Passing this explicitly is what stops
      // opening the app from clearing a badge an unread room or invite earned.
      badge: attentionBadgeTotal(useAppStore.getState(), entries.length),
    })
    .catch(() => {})
}
bridge.on('subscription.updated', ({ subscribed }) => {
  useAppStore.getState().setSubscribed(subscribed)
})
// User tapped a push notification banner. Find the agent for the
// position the request came from and push the agent_detail screen.
// Cold launch / resume can race: the tap fires before the WS reconnect
// has replayed `ai_agent_instances_list`, so the in-memory lookup
// returns nothing. Stash the position id; the agentInstances
// subscriber below resolves it as soon as a matching agent lands.
bridge.on('push.tapped', ({ requestID, organizationPositionID, organizationID, roomID, roomName, threadRootID }) => {
  const s = useAppStore.getState()
  s.markPushEvent()

  // Re-point the device at the household the destination lives in,
  // before anything below tries to resolve an id against the current
  // household's lists. Notifications are member-scoped (you hear about
  // every household you belong to) while the app's state is
  // device-scoped (one selected household), so a tap is the one moment
  // those two have to be reconciled.
  //
  // Only when the push actually has somewhere to go. Household-activity
  // pushes ("Alice joined your household", host enrolled/revoked) carry
  // organization_id for context but no destination id — switching on
  // those would silently move the user to a different household and then
  // navigate nowhere, leaving them somewhere they never asked to be with
  // nothing on screen to explain it. A household switch has to be
  // something the user can see happen.
  if (requestID || roomID || organizationPositionID) {
    switchOrganizationForPush(organizationID)
  }

  // requestID present = the push was a permission_request. Set the
  // single-ask overlay state so PermissionOverlay surfaces THIS ask
  // specifically (deep-link). Also kick a fetch so cross-org pendings
  // that aren't in the store land soon enough for the overlay to
  // find a matching request.
  if (requestID) {
    s.setSingleAskRequestID(requestID)
    // Ensure the tapped request lands in the store. If WS is OPEN
    // now, fetch directly. If still connecting (typical cold-launch-
    // from-push), the awaiting flag set by the fg event handler
    // will trigger the refresh after onopen — set it here too in
    // case this push tap is the only signal we get.
    triggerForegroundRefresh()
  }

  // Chat message tap — navigate directly to the room. Safe to do
  // immediately even when the switch above is still in flight: the
  // room screen loads its own messages via list_chat_messages, which
  // authorizes on household membership rather than on the device's
  // selected household. roomName rides the push for the same reason —
  // the local room list won't contain a cross-household room until the
  // switch lands.
  if (roomID) {
    const name = roomName || s.chatRooms.find((r) => r.id === roomID)?.name || 'Chat'
    // A reply in a thread opens the thread over its room (the household
    // switch already happened above). Only newer shells forward the id.
    if (threadRootID) {
      openChatThread(undefined, roomID, name, threadRootID)
      return
    }
    // Clear any stale nav stack entry for the same room, then push fresh
    // so the room re-mounts and scrolls to the latest message.
    const alreadyOpen = s.navStack.some((e) => e.kind === 'chat_room' && e.roomID === roomID)
    if (!alreadyOpen) s.pushNav({ kind: 'chat_room', roomID, roomName: name })
    return
  }

  if (!organizationPositionID) return
  if (!resolvePushNavigation(organizationPositionID)) {
    s.setPendingPushPositionID(organizationPositionID)
  }
})
// Foreground/silent push receipt — also counts toward the announcement
// race-debounce so we don't splash a CTA on top of a banner being
// processed.
bridge.on('push.received', () => {
  useAppStore.getState().markPushEvent()
})

// Invitation deep link. The token in a legacy /accept?t=<token> URL is
// deliberately ignored: invitations are resolved server-side from the
// signed-in user's verified email, so the link's only job is "open the
// app and show me my invitations". That also means a link forwarded to
// the wrong person does nothing at all — they'll see their own
// invitations, which is usually none.
bridge.on('invite.link', ({ email }) => {
  const s = useAppStore.getState()
  s.setInvitationsLinkPending(true)
  // Prefill hint for the registration screen. Only useful for a not-yet-
  // enrolled invitee (a signed-in user skips the email step); harmless
  // otherwise.
  if (email) s.setInvitePrefillEmail(email)
})

// A scanned kiosk QR (/pair?code=…) hands the code to the store; App claims it once
// the user is enrolled (it's a no-op until then).
bridge.on('pair.link', ({ code }) => {
  useAppStore.getState().setPendingPairCode(code)
})

// Whenever agentInstances refreshes (initial WS reply, periodic
// refresh, agent spawn), retry any deferred push-tap navigation. Beats
// dispatching off a specific message type — works regardless of which
// arrival re-populates the list.
useAppStore.subscribe((s, prev) => {
  const pos = s.pendingPushPositionID
  if (!pos) return
  if (s.agentInstances === prev.agentInstances) return
  if (resolvePushNavigation(pos)) {
    useAppStore.getState().setPendingPushPositionID(null)
  }
})

// Whenever the live pending-request set changes, hand it to the native
// shell so it can reconcile Notification Center against the truth.
// Catches silent permission_resolved pushes that iOS throttled — the
// banner sits stale on the lockscreen until reconciliation prunes it.
// The bridge call is best-effort; resolves to a no-op on platforms
// that don't expose notifications.reconcile (e.g. dev browser).
useAppStore.subscribe((s, prev) => {
  if (s.pendingRequests === prev.pendingRequests) return
  reconcileNotifications()
})

// Hand the live pending set to the native shell: prunes stale banners and
// sets the app-icon badge to the count. Authoritative in the sense that
// matters — it fires on a set that just changed, while the socket is up and
// delivering permission_resolved frames promptly, so it's what makes the
// badge move in real time as asks land and get answered.
//
// It agrees with the server's approver-scoped count because every foreground
// refresh now prunes pendingRequests down to the canonical list (see
// refreshPendingApprovals). Before that pruning existed the two could
// drift apart permanently, and this subscriber — firing last on reconnect —
// won, which is how a resolved-elsewhere ask kept the badge lit.
function reconcileNotifications(): void {
  const st = useAppStore.getState()
  const activeRequestIDs = st.pendingRequests.map((r) => r.id)
  void bridge
    .call('notifications.reconcile', {
      activeRequestIDs,
      authoritative: true,
      badge: attentionBadgeTotal(st, activeRequestIDs.length),
    })
    .catch(() => {})
}

// Returns true if an agent matched the position id and we navigated (or
// were already there). Returns false if no match exists yet — caller
// decides whether to defer.
function resolvePushNavigation(positionID: string): boolean {
  const s = useAppStore.getState()
  const agent = s.agentInstances.find(
    (a) => a.organization_position_id === positionID,
  )
  if (!agent) return false
  const top = s.navStack[s.navStack.length - 1]
  if (top?.kind === 'agent_detail' && top.agentInstanceID === agent.id) {
    return true
  }
  s.pushNav({
    kind: 'agent_detail',
    agentInstanceID: agent.id,
    positionID,
    source: 'push',
  })
  return true
}

// ---- Incoming message dispatch -----------------------------------------

// Server → device messages. Shape matches what permit-cloud sends; only
// fields the UI consumes are extracted. Unknown message types are logged
// but otherwise ignored so the server can add new types without breaking
// the client.
//
// Exported for tests. The connection-management code in this same file is
// normalizePermissionRequest turns the raw wire frame into the
// PermissionRequest shape the store + renderers consume, with
// subject_kind + subject always populated. For pre-1h frames (or
// any forward-compat frame without subject_kind) it synthesizes a
// tool_call subject from the legacy top-level fields, so the
// renderer dispatch in PermissionRequestCard can always switch on
// subject_kind without re-checking each downstream code path.
//
// See hearth-cmd-cli/docs/resource-plugins-1h-plan.md §5a.
function normalizePermissionRequest(msg: any): import('../store/types').PermissionRequest {
  const rawKind = msg.subject_kind
  const subjectKind: import('../store/types').PermissionSubjectKind =
    rawKind === 'external_resource_invoke'
      ? 'external_resource_invoke'
      : rawKind === 'pick'
        ? 'pick'
        : 'tool_call'

  let subject: import('../store/types').PermissionSubject
  if (subjectKind === 'external_resource_invoke' && msg.subject) {
    subject = msg.subject as import('../store/types').ExternalResourceInvokeSubject
  } else if (subjectKind === 'pick' && msg.subject) {
    subject = msg.subject as import('../store/types').PickSubject
  } else if (subjectKind === 'tool_call' && msg.subject) {
    subject = msg.subject as import('../store/types').ToolCallSubject
  } else {
    // Legacy fixture / pre-1h frame — synthesize from top-level fields.
    subject = {
      tool_name: msg.tool_name ?? '',
      tool_input: msg.tool_input ?? {},
      segments: msg.segments ?? undefined,
      outside_project: msg.outside_project ?? undefined,
    }
  }

  return {
    id: msg.request_id ?? msg.id,
    ai_agent_instance_id: msg.ai_agent_instance_id,
    organization_position_id: msg.organization_position_id,
    tool_name: msg.tool_name ?? '',
    tool_input: msg.tool_input ?? {},
    timeout: msg.timeout ?? null,
    parked: msg.parked === true,
    received_at: Date.now(),
    segments: msg.segments ?? null,
    agent_name: msg.agent_name,
    position_name: msg.position_name,
    organization_name: msg.organization_name,
    organization_id: msg.organization_id,
    source_label: msg.source_label,
    subject_kind: subjectKind,
    subject,
  }
}

// the only production caller; tests drive handleMessage directly with
// crafted payloads to exercise the dispatch table without standing up a
// real WebSocket.
export function handleMessage(msg: any) {
  // Fire one-shot handlers (e.g. connection_authorized from OAuth callback).
  if (msg?.type) {
    const hs = oneshotHandlers.get(msg.type)
    if (hs && hs.length > 0) {
      const toFire = hs.splice(0)
      hs.length === 0 && oneshotHandlers.delete(msg.type)
      toFire.forEach((h) => h(msg))
    } else if (msg.type === 'connection_authorized') {
      console.warn('[oauth] connection_authorized arrived but no handler registered')
    }
  }
  // Resolve pending wsRequest() callers first. Doesn't short-circuit the
  // switch below — list responses like `hosts_list` still need to land in
  // the store even when a wizard is listening for them.
  const consumedByPending = deliverPendingRequest(msg)
  const s = useAppStore.getState()

  // Which household a household-scoped list frame describes. The server
  // stamps organization_id on every such frame; the fallback covers a
  // server older than that change, and wsRequest responses which are
  // always issued against the household we're currently on. Returns ''
  // when there's no household context at all, which the store setters
  // treat as "nothing to file".
  const frameOrgID = (): string =>
    (msg.organization_id as string | undefined) || s.organizationID || ''

  switch (msg?.type) {
    case 'pong':
      // Heartbeat reply. Normally already consumed by the ping's wsRequest via
      // deliverPendingRequest above; this silences the debug log for a pong that
      // arrives after its request timed out (resolver already gone).
      break
    case 'ai_agent_instances_list':
      s.replaceAgentInstances(frameOrgID(), msg.ai_agent_instances ?? [])
      // Deferred push-tap nav resolves via the agentInstances
      // subscriber registered at module load (search for
      // `pendingPushPositionID`). No per-message handling needed.
      return
    case 'ai_agent_instance_created': {
      // If the user triggered a temp spawn from the FAB, jump straight
      // into the transcript. Server errors come back with a non-empty
      // `error` field — surface those instead of navigating.
      const pending = s.pendingTempSpawn
      if (!pending) return
      s.clearPendingTempSpawn()
      if (msg.error) {
        s.setTempSpawnError(String(msg.error))
        return
      }
      if (msg.spawn_error) {
        s.setTempSpawnError(String(msg.spawn_error))
        return
      }
      const instance = msg.ai_agent_instance
      if (instance?.id && instance?.organization_position_id) {
        s.pushNav({
          kind: 'agent_detail',
          agentInstanceID: instance.id,
          positionID: instance.organization_position_id,
        })
      }
      return
    }
    case 'organization_positions_list':
      s.replacePositions(frameOrgID(), msg.organization_positions ?? [])
      return
    // Response to an explicit list_organization_positions request
    // (the *_list variant above is a push from the org-context switch
    // path). Both update the same slice; this case lets the debug
    // PositionsSheet refresh on demand.
    case 'organization_positions':
      s.replacePositions(frameOrgID(), msg.organization_positions ?? [])
      return
    case 'organizations_list':
      s.replaceOrganizations(msg.organizations ?? [], msg.owned_org_cap as number | undefined)
      return
    case 'organization_renamed': {
      // Companion to organizations_list re-push. The list keeps state
      // in sync; this is the delta clients use to render a toast.
      const newName = String(msg.name ?? '')
      const orgs = s.availableOrganizations
      // Use the cached old name when available so the toast can show
      // both. List re-push hasn't necessarily landed yet, so the old
      // entry should still be in the store.
      const old = orgs.find((o) => o.id === msg.organization_id)
      const text = old?.name && old.name !== newName
        ? `${old.name} renamed to ${newName}`
        : newName
          ? `Renamed to ${newName}`
          : 'Household renamed'
      s.pushToast(text, 'info')
      return
    }
    case 'household_deleted': {
      // A household this person belonged to was permanently deleted. The
      // relay has already moved their devices to another household (or none)
      // and re-pushes organizations_list; this frame is the telling. Screens
      // pushed inside the deleted household point at rows that no longer
      // exist, so they close. The owner who deleted it is looking at the
      // wizard's last step, which says it all.
      if (msg.by_you) return
      if (s.organizationID === msg.organization_id) {
        useAppStore.setState({ navStack: [] })
      }
      const name = String(msg.name ?? '') || 'A household'
      const who = String(msg.actor_name ?? '')
      s.pushToast(who ? `${who} deleted ${name}` : `${name} was deleted`, 'warning')
      return
    }
    case 'organization_member_added': {
      const member = s.organizationMembers.find(
        (m) => m.human_user_id === msg.human_user_id,
      )
      const who = member?.name || member?.email || 'Someone'
      const org = s.availableOrganizations.find(
        (o) => o.id === msg.organization_id,
      )
      const text = org?.name ? `${who} joined ${org.name}` : `${who} joined`
      s.pushToast(text, 'success')
      return
    }
    case 'organization_member_removed': {
      const member = s.organizationMembers.find(
        (m) => m.human_user_id === msg.human_user_id,
      )
      const who = member?.name || member?.email || 'Someone'
      const org = s.availableOrganizations.find(
        (o) => o.id === msg.organization_id,
      )
      const text = org?.name
        ? `${who} was removed from ${org.name}`
        : `${who} was removed`
      s.pushToast(text, 'warning')
      return
    }
    case 'rules_list':
      s.replaceRules(frameOrgID(), msg.rules ?? [])
      return
    case 'scheduled_triggers_list':
      s.replaceScheduledTriggers(frameOrgID(), msg.scheduled_triggers ?? [])
      return
    case 'provisioning_proposals_list':
      // The onboarding review queue (pushed live on create/resolve, and fetched
      // on connect / org switch). Filed per household like other tier-2 lists.
      s.replaceProvisioningProposals(frameOrgID(), msg.provisioning_proposals ?? [])
      return
    case 'provisioning_proposal_created':
      // A proposal that just landed (deterministic inline, sweep, or Facilitator).
      // Merge it straight into the queue so the "N suggestions" banner appears even
      // when the originating wsRequest response was lost to the spawn-frame tag
      // race, or the list push was missed during a reconnect. Idempotent.
      if (msg.provisioning_proposal?.id) {
        s.upsertProvisioningProposal(
          msg.provisioning_proposal.organization_id ?? frameOrgID(),
          msg.provisioning_proposal,
        )
      }
      return
    case 'hosts_list':
      s.replaceHosts(frameOrgID(), msg.hosts ?? [])
      return
    case 'my_io_devices':
      s.replaceIODevices(msg.io_devices ?? [])
      return
    case 'display_screens_list':
      s.replaceDisplayScreens(msg.screens ?? [])
      return
    case 'working_directories':
      s.replaceWorkingDirectories(msg.working_directories ?? [])
      return
    case 'organization_members':
      s.replaceOrganizationMembers(msg.members ?? [])
      return
    case 'invites':
      s.replaceInvites(msg.invites ?? [])
      return
    case 'my_invites':
      s.replaceMyInvites(msg.invites ?? [])
      // Invites are a badge input now, so settle the app-icon badge when the
      // set changes (a new invite lands, or one is accepted/declined on another
      // device) rather than waiting for the next ask or foreground.
      reconcileNotifications()
      return
    case 'secrets_list_response':
      s.replaceSecrets(msg.secrets ?? [])
      return
    case 'agent_job_descriptions':
      s.replaceAgentJobDescriptions(msg.agent_job_descriptions ?? [])
      return
    case 'resource_connections_list':
      s.replaceResourceConnections(msg.resource_connections ?? [])
      return
    case 'plugin_installs_list':
      s.replacePluginInstalls(msg.plugin_installs ?? [])
      return
    case 'plugin_install_result':
      // Notify listeners first, then refresh. A success changes the list, so
      // the refetch is what makes the new version appear; a failure does not,
      // which is exactly why the message carries the error text.
      emitPluginInstallResult(msg as PluginInstallResult)
      if (msg.ok) fetchPluginInstalls()
      return
    case 'permission_request': {
      const req = normalizePermissionRequest(msg)
      s.enqueueRequest(req)
      // Nudge the user. Warning-style haptic matches the native
      // UINotificationFeedbackGenerator(.warning) fired by
      // HearthService when a request lands.
      bridge.call('haptic.warning').catch(() => {})
      // In a voice session, a haptic is not enough: the phone may be face-up on
      // a table and the person is listening, not looking.
      speakApprovalLine('you', req.ai_agent_instance_id)
      // If the user is currently viewing this agent's detail screen,
      // pop the overlay so they don't have to context-switch out of
      // their conversation to find the ask. Asks for OTHER agents
      // stay quiet — banner + haptic only — so a request from a
      // background agent doesn't yank them out of what they're doing
      // with the foreground one. Mirror condition with
      // singleAskRequestID check so we don't override an already-
      // displayed overlay (which could be from a push tap mid-typing).
      const top = s.navStack[s.navStack.length - 1]
      if (!s.singleAskRequestID && req.ai_agent_instance_id) {
        const isAgentDetail =
          top?.kind === 'agent_detail' &&
          top.agentInstanceID === req.ai_agent_instance_id
        // Chat room: any agent request pops through — the user just
        // @mentioned someone and is actively expecting a response.
        const isChatRoom = top?.kind === 'chat_room'
        if (isAgentDetail || isChatRoom) {
          s.setSingleAskRequestID(req.id)
        }
      }
      return
    }
    case 'cancel_request':
      s.dequeueRequest(msg.request_id ?? msg.id)
      return
    case 'permission_resolved': {
      // Another device (or this one) responded. Drop any pending entry
      // still showing this request so the prompt doesn't sit stale.
      // dequeueRequest is idempotent, so no need to guard against the
      // self-broadcast we get back after submitting locally.
      const rid = msg.request_id ?? msg.id
      s.dequeueRequest(rid)
      // Initiator-informational variant (X2): carries a disposition so we can
      // settle the local user's passive "pending — X is handling" banner. The
      // actionable resolution frame has no disposition and this is a no-op.
      const rr = msg as { disposition?: string; body?: string }
      if (rr.disposition && rid) {
        const status =
          rr.disposition === 'allowed' ? 'allowed' : rr.disposition === 'denied' ? 'denied' : 'expired'
        s.settleInitiatorNotice(rid, status, rr.body ?? '')
      }
      return
    }
    case 'permission_pending': {
      // Passive FYI to the human who triggered an action gated on someone
      // else (X2). Not actionable — feeds the InitiatorNotices banner, never
      // the approve/deny overlay. Refreshed in place as the ladder escalates.
      const raw = msg as { request_id?: string; tool?: string; body?: string; ts?: string }
      if (!raw.request_id) return
      // Someone ELSE is deciding, so the honest line is the household one.
      speakApprovalLine('household')
      s.upsertInitiatorNotice({
        requestId: raw.request_id,
        tool: raw.tool ?? '',
        status: 'pending',
        body: raw.body ?? '',
        ts: raw.ts ? Date.parse(raw.ts) : Date.now(),
      })
      return
    }
    case 'permission_dispatch_notice': {
      // Ephemeral: "right now these people are being asked." Synthesize a
      // transient transcript row so currently-open chat windows see it
      // inline. Not persisted server-side — disappears on reload, leaving
      // only the durable permission_resolved row that lands later.
      const raw = msg as {
        ai_agent_instance_id?: string
        request_id?: string
        tool?: string
        notified?: { id?: string; name?: string }[]
        authorized?: { id?: string; name?: string }[]
        failed?: { id?: string; name?: string }[]
        body?: string
        ts?: string
      }
      if (!raw.ai_agent_instance_id) return
      // Build the same hearth/1 envelope shape the persistent system events
      // use, so SystemEventRow renders this row identically. `failed` lists
      // approvers whose phone tokens were on file but every transport
      // errored — the body string already names them, but renderers may
      // want the structured list for styling.
      const envelope = {
        kind: 'system_event',
        event: 'permission_dispatch',
        request_id: raw.request_id,
        tool: raw.tool,
        notified: raw.notified ?? [],
        authorized: raw.authorized ?? [],
        failed: raw.failed ?? [],
      }
      const text = 'hearth/1 ' + JSON.stringify(envelope) + '\n\n' + (raw.body ?? '')
      const timestamp = raw.ts ? Date.parse(raw.ts) : Date.now()
      s.appendTranscript([
        {
          id: `dispatch-${raw.request_id ?? timestamp}`,
          ai_agent_instance_id: raw.ai_agent_instance_id,
          type: 'user',
          text,
          tool_name: null,
          tool_input: null,
          timestamp,
        },
      ])
      return
    }
    case 'clear_transcript':
      // Scoped to one agent — the server stamps ai_agent_instance_id on the
      // frame (it broadcasts per-instance before a reconnect replay). Clearing
      // globally here would wipe every other agent's buffered transcript.
      s.clearTranscript((msg as { ai_agent_instance_id?: string }).ai_agent_instance_id)
      return
    case 'transcript_entry': {
      // Wire format carries no stable id; fabricate one from timestamp +
      // event + text hash so React keys are deterministic and the store's
      // dedup catch-all works. Map `event` → our internal `type`.
      const raw = msg as {
        ai_agent_instance_id?: string
        event?: string
        text?: string
        tool_name?: string
        tool_input?: Record<string, unknown>
        message?: string
        timestamp?: string
        seq?: number
        sub?: number
        block?: number
        mid?: string
        voice?: { spoken?: string; listen?: boolean }
      }
      // Drop frames with no agent id — they can't be bucketed and a '' bucket
      // would collide across agents. (Live frames always carry it; this only
      // guards malformed/legacy input.)
      if (!raw.ai_agent_instance_id) return
      const text = raw.text ?? raw.message ?? ''
      // Prefer the host/relay-stamped ordering key when present: id is the exact
      // "seq.sub.block" tuple (so two identical repeated entries no longer
      // collide), and the store orders on the numeric parts. Fall back to the
      // content hash + arrival order for un-stamped frames (older daemons).
      const hasSeq = typeof raw.seq === 'number'
      const id = hasSeq
        ? `${raw.seq}.${raw.sub ?? 0}.${raw.block ?? 0}`
        : `${raw.timestamp ?? ''}|${raw.event ?? ''}|${raw.tool_name ?? ''}|${(text || '').slice(0, 80)}`
      s.appendTranscript([
        {
          id,
          ai_agent_instance_id: raw.ai_agent_instance_id,
          type: (raw.event as any) ?? 'text',
          text,
          tool_name: raw.tool_name ?? null,
          tool_input: raw.tool_input ?? null,
          timestamp: raw.timestamp ? Date.parse(raw.timestamp) : Date.now(),
          seq: raw.seq,
          sub: raw.sub,
          block: raw.block,
          // Set on queued_user frames: the id the message can be addressed by
          // later, if the host reports it undeliverable.
          mid: raw.mid,
          // Set on agent text the household actually HEARD. The relay parsed the
          // agent's marker and stripped it, so `text` is already clean — this is
          // just what was voiced, and whether the mic was left open.
          voice: raw.voice?.spoken
            ? { spoken: raw.voice.spoken, listen: raw.voice.listen === true }
            : undefined,
        },
      ])
      return
    }
    case 'relay_input_ack': {
      // Reconcile the optimistic bubble the input bar showed on send. On
      // success we attach the mid, which is what lets the transcript replace
      // this row exactly once when the real turn lands. On refusal we flip it
      // to failed — a message the relay wouldn't take must not sit there
      // looking like it's on its way.
      const raw = msg as {
        client_request_id?: string
        ai_agent_instance_id?: string
        mid?: string
        error?: string
      }
      if (!raw.client_request_id || !raw.ai_agent_instance_id) return
      s.patchTranscriptEntry(
        raw.ai_agent_instance_id,
        'local-' + raw.client_request_id,
        raw.error
          ? { send_state: 'failed' }
          : { mid: raw.mid, send_state: undefined },
      )
      return
    }
    case 'message_undeliverable': {
      // The host-side inbox gave up on this message — quarantined after
      // repeated failed delivery, or expired waiting for the agent. Stop the
      // row claiming it's on its way.
      const raw = msg as {
        ai_agent_instance_id?: string
        mid?: string
        message?: string
      }
      if (!raw.ai_agent_instance_id || !raw.mid) return
      s.markTranscriptUndeliverable(
        raw.ai_agent_instance_id,
        raw.mid,
        raw.message ?? 'This was never delivered to the agent.',
      )
      return
    }
    case 'history_entry': {
      // Wire format: { type, entry: { request_id, tool_name, outcome,
      // responded_at, ai_agent_instance_id, organization_position_id,
      // tool_input?, responder_kind?, responder_id?, responder_name? } }.
      // organization_position_id is included by recordRequestOutcome; fall
      // back to resolving from the agent directory for legacy frames.
      const raw = (msg.entry ?? msg) as {
        request_id?: string
        tool_name?: string
        outcome?: string
        responded_at?: string
        ai_agent_instance_id?: string
        organization_position_id?: string
        tool_input?: Record<string, unknown>
        responder_kind?: string
        responder_id?: string
        responder_name?: string
      }
      let positionID = raw.organization_position_id
      if (!positionID) {
        positionID = s.agentInstances.find(
          (a) => a.id === raw.ai_agent_instance_id,
        )?.organization_position_id
      }
      if (!positionID) {
        console.debug('ws: history_entry with unknown position for agent', raw.ai_agent_instance_id)
        return
      }
      s.appendHistory({
        id: raw.request_id ?? `${raw.responded_at}|${raw.tool_name}`,
        organization_position_id: positionID,
        tool_name: raw.tool_name ?? '',
        tool_input: raw.tool_input ?? {},
        outcome: (raw.outcome as any) ?? 'allow',
        occurred_at: raw.responded_at ? Date.parse(raw.responded_at) : Date.now(),
        responder_kind: raw.responder_kind as any,
        responder_id: raw.responder_id,
        responder_name: raw.responder_name,
      })
      return
    }
    case 'agent_history_response': {
      // Response to list_agent_history. Merge items into positionHistory and
      // mark the position as loaded so HistorySegment stops showing a spinner.
      const positionID: string = msg.organization_position_id ?? ''
      if (!positionID) return
      const items: HistoryItem[] = (msg.items ?? []).map((r: any) => ({
        id: r.id ?? r.request_id ?? '',
        organization_position_id: r.organization_position_id ?? positionID,
        tool_name: r.tool_name ?? '',
        tool_input: r.tool_input ?? {},
        outcome: r.outcome ?? 'allow',
        occurred_at: r.occurred_at ? Date.parse(r.occurred_at) : 0,
        responder_kind: r.responder_kind,
        responder_id: r.responder_id,
        responder_name: r.responder_name,
      }))
      s.mergeHistory(positionID, items)
      s.markHistoryLoaded(positionID)
      return
    }
    case 'announcements':
      s.setAnnouncements(Array.isArray(msg.announcements) ? msg.announcements : [])
      return
    case 'announcement':
      if (msg.announcement?.id) {
        s.enqueueAnnouncement(msg.announcement)
      }
      return
    case 'my_pro_subscriptions': {
      // Server pushes this both as a wsRequest response and proactively on
      // subscription state changes (renewals, expirations, webhook events).
      const active = Array.isArray(msg.subscriptions) ? msg.subscriptions[0] : undefined
      s.setSubscribedProductID(active?.product_id ?? null)
      s.setSubscribedPlatform(active?.platform ?? null)
      s.setSubscribedExpiresAt(active?.expires_at ?? null)
      return
    }
    case 'pending_approvals_list': {
      // Server pushes this both as a wsRequest response and as a proactive
      // reconcile on connect. Merge entries that aren't already in the store.
      const entries: PendingApprovalEntry[] = Array.isArray(msg.entries) ? msg.entries : []
      const existing = new Set(s.pendingRequests.map((p) => p.id))
      for (const e of entries) {
        if (!existing.has(e.request_id)) {
          s.enqueueRequest(normalizePendingApprovalEntry(e))
        }
      }
      return
    }
    case 'offer_status':
      // Server emits `status: "active" | "expired" | "invalid"` and an
      // optional `error` string. Treat anything other than "active" as
      // not-active so the store reflects revocations / expirations
      // pushed mid-session.
      s.setActiveOffer(msg.status === 'active')
      if (msg.status === 'active') {
        s.setOfferError(null)
      } else if (typeof msg.error === 'string' && msg.error) {
        s.setOfferError(msg.error)
      }
      return
    case 'chat_rooms_list':
      // replaceChatRooms adopts each room's server-computed unread_count as
      // authoritative (from the per-human read watermark), so a reconnect
      // replay now REFRESHES unread from server truth rather than clobbering a
      // client guess — the W7 flap that wiped genuine unread predates the
      // server knowing read state, and the server value is the genuine count.
      // Rooms an older server omits unread_count for keep the optimistic
      // client counter incremented on live messages below.
      s.replaceChatRooms(frameOrgID(), msg.chat_rooms ?? [])
      return
    case 'chat_message': {
      const m = msg.message
      if (m?.room_id) {
        // Every message (channel or thread) lives in its room's list; the
        // channel and thread screens filter it. See chat/threads.ts.
        s.appendChatMessages(m.room_id, [{ ...m, timestamp: parseServerTime(m.created_at) }])
        const isRoomOpen = useAppStore.getState().navStack
          .some((e) => e.kind === 'chat_room' && e.roomID === m.room_id)
        // A plain thread reply doesn't make its room unread (Slack's rule, and
        // the server's badge agrees); only what the channel shows does.
        const channelVisible = !m.thread_root_id || m.also_in_channel
        if (!isRoomOpen && channelVisible) s.incrementChatUnread(m.room_id)
      }
      return
    }
    case 'attention_counts':
      // A setup suggestion landed or was applied/declined in one of your
      // households: the server's fresh count, so the bell moves now.
      if (typeof msg.pending_suggestion_count === 'number') {
        s.setPendingSuggestionCount(msg.pending_suggestion_count)
      }
      reconcileNotifications()
      return
    case 'chat_unread_counts':
      // A followed thread got a reply (or similar): the server's fresh chat
      // halves of the badge, so the bell and app icon move now.
      if (typeof msg.unread_room_count === 'number') s.setUnreadRoomCount(msg.unread_room_count)
      if (typeof msg.unread_thread_count === 'number') s.setUnreadThreadCount(msg.unread_thread_count)
      reconcileNotifications()
      // Which rooms/threads: the counts alone can't mark the right rows.
      scheduleAttentionRoomsRefresh()
      return
    case 'chat_message_also_in_channel':
      // A thread reply was sent to the room after the fact: it now shows in the
      // channel view too (the same row; see threads.ts).
      if (msg.room_id && msg.message_id) {
        s.updateChatMessage(msg.room_id, msg.message_id, {
          also_in_channel: true,
          thread_root_preview: msg.thread_root_preview ?? null,
        })
      }
      return
    case 'chat_thread_summary':
      // A thread gained or lost a reply: refresh the summary under its first
      // message, if we hold it.
      if (msg.room_id && msg.root_id) {
        const thread = msg.thread as ThreadSummary | undefined
        s.updateChatMessage(msg.room_id, msg.root_id, {
          thread: thread && thread.reply_count > 0 ? thread : null,
        })
      }
      return
    case 'chat_message_deleted':
      if (msg.room_id && msg.message_id) {
        s.updateChatMessage(msg.room_id, msg.message_id, {
          deleted_at: new Date().toISOString(),
          text: '',
        })
      }
      return
    case 'chat_message_edited':
      if (msg.room_id && msg.message_id) {
        s.updateChatMessage(msg.room_id, msg.message_id, {
          text: (msg.text as string) ?? '',
          edited_at: (msg.edited_at as string) ?? new Date().toISOString(),
        })
      }
      return
    case 'chat_reaction_update':
      if (msg.room_id && msg.message_id) {
        s.updateChatMessage(msg.room_id, msg.message_id, { reactions: msg.reactions ?? [] })
      }
      return
    default:
      if (!consumedByPending) console.debug('ws: unhandled message type', msg?.type)
  }
}

// ---- Outgoing helpers --------------------------------------------------

export function sendPermissionResponse(args: {
  requestID: string
  behavior: 'allow' | 'deny' | 'deny_stop' | 'always_allow' | 'always_deny'
  expiresAt?: string | null
  interrupt?: boolean
  // Populated when responding to a SubjectKindPick. The option `id` the
  // user chose; server interprets per pick_kind (e.g. resource_binding
  // interprets it per pick_kind). Ignored by other subject kinds.
  selectedOptionID?: string
  // Free-text input when subject.allow_free_text is true and the user
  // typed in the "Other" field. Server-side pick_kind handler decides
  // what to do with it.
  freeText?: string
}) {
  const payload: Record<string, unknown> = {
    type: 'permission_response',
    request_id: args.requestID,
    behavior: args.behavior,
    expires_at: args.expiresAt ?? null,
    interrupt: args.interrupt ?? false,
  }
  if (args.selectedOptionID) {
    payload.selected_option_id = args.selectedOptionID
  }
  if (args.freeText) {
    payload.free_text = args.freeText
  }
  // Returns whether the frame reached an OPEN socket. The overlay uses a false
  // return to trigger the HTTP fallback (POST /response) instead of dismissing
  // the request as answered — otherwise a response sent over a closed/half-open
  // socket vanishes silently, and a lost deny_stop leaves a runaway agent
  // running while the user believes they stopped it.
  return sendMessage(payload, {
    kind: 'permission_request',
    id: args.requestID,
    action: args.behavior,
  })
}

// Explicit punt (O12, docs/authz-policy-model.md §3.5): a current approver
// escalating a request instead of deciding it. The server advances the
// escalation ladder to the next rung (adding approvers) without resolving — a
// punt never allows or denies. A no-op server-side on the final rung (nobody
// left to escalate to). Returns whether the frame reached an OPEN socket.
export function sendPermissionPunt(requestID: string) {
  return sendMessage(
    { type: 'permission_punt', request_id: requestID },
    { kind: 'permission_request', id: requestID, action: 'punt' },
  )
}

// Returns whether the frame reached an OPEN socket. The input bar uses a false
// return to restore the typed text and show an error instead of clearing the
// composer — otherwise a message to an agent vanishes silently on a
// closed/half-open socket, exactly like the chat-send drop.
export function sendRelayInput(
  aiAgentInstanceID: string,
  text: string,
  mentions?: { id: string; name?: string }[],
  clientRequestID?: string,
  // Set when the text was SPOKEN rather than typed. The server then applies the
  // same voice preamble and envelope context it applies to a Home Assistant
  // puck, which is what makes the agent wrap its reply in <hearth-voice> so the
  // phone has something to say aloud. Omitted entirely when false: an older
  // relay ignores unknown fields, so a client ahead of its server degrades to
  // an ordinary typed message rather than breaking.
  // See docs/phone-voice-endpoint-plan.md §3.3.
  spoken?: boolean,
): boolean {
  const payload: Record<string, unknown> = {
    type: 'relay_input',
    ai_agent_instance_id: aiAgentInstanceID,
    text,
  }
  if (spoken) {
    payload.spoken = true
  }
  // Opting in to relay_input_ack, which echoes the mid the server minted. The
  // caller uses it to reconcile the message it just showed the user against the
  // transcript entry that eventually carries the same mid — which can be a
  // while, since the host-side inbox holds a message until the agent is free.
  if (clientRequestID) {
    payload.client_request_id = clientRequestID
  }
  if (mentions && mentions.length > 0) {
    payload.mentions = mentions
  }
  return sendMessage(payload, {
    kind: 'ai_agent_instance',
    id: aiAgentInstanceID,
    action: 'relay_input',
  })
}

// Ask the server to relay a transcript-history dump from the daemon owning
// the agent. The daemon reads its on-disk JSONL and replays the last ~500
// entries through the same `transcript_entry` channel as live tail, so the
// store's existing dedup handles overlap with already-cached entries. Silent
// no-op if the host is offline.
export function sendRequestTranscriptHistory(aiAgentInstanceID: string) {
  sendMessage(
    {
      type: 'request_transcript_history',
      ai_agent_instance_id: aiAgentInstanceID,
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'read_transcript' },
  )
}

// Ask the server for recent request_outcomes rows for a position. Called on
// fresh load (since=undefined → server returns up to 200 most recent) and on
// reconnect (since=RFC3339 → server returns only the gap since that timestamp).
// The response lands as an `agent_history_response` frame handled below.
export function sendRequestAgentHistory(positionID: string, since?: string) {
  sendMessage(
    {
      type: 'list_agent_history',
      organization_position_id: positionID,
      ...(since ? { timestamp: since } : {}),
    },
    { kind: 'organization_position', id: positionID, action: 'read_history' },
  )
}

export function sendAnnouncementDismiss(announcementID: string) {
  sendMessage(
    { type: 'announcement_dismiss', announcement_id: announcementID },
    { kind: 'announcement', id: announcementID, action: 'dismiss' },
  )
}

// Returns whether the kill frame reached an OPEN socket. Pull-the-Plug is a
// safety action — a caller MUST surface a false return ("couldn't reach the
// server, try again") rather than let the user believe a runaway agent was
// halted when the frame was dropped on a closed/half-open socket.
export function sendKillAgent(aiAgentInstanceID: string): boolean {
  return sendMessage(
    { type: 'kill_agent_instance', ai_agent_instance_id: aiAgentInstanceID },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'kill' },
  )
}

export function sendRetireAgent(aiAgentInstanceID: string) {
  sendMessage(
    { type: 'retire_agent_instance', ai_agent_instance_id: aiAgentInstanceID },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'retire' },
  )
}

/// Destroy is the temp-only counterpart to Retire. Server-side it cascades
/// to the temp position + working_directory rows, and the daemon
/// rm -rf's the on-disk working directory. Server refuses if the agent
/// isn't is_temp=1.
export function sendDestroyAgent(aiAgentInstanceID: string) {
  sendMessage(
    { type: 'destroy_agent_instance', ai_agent_instance_id: aiAgentInstanceID },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'destroy' },
  )
}

/// Wake / Re-spawn / Retry — all map to wake_ai_agent_instance server-side.
/// The server resets pid_status to 'never_spawned' and pushes the wake to
/// the owning daemon; the UI chooses the label (Wake vs Re-spawn).
export function sendWakeAgent(aiAgentInstanceID: string) {
  sendMessage(
    {
      type: 'wake_ai_agent_instance',
      data: { id: aiAgentInstanceID },
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'wake' },
  )
}

export function sendSleepAgent(aiAgentInstanceID: string) {
  sendMessage(
    {
      type: 'sleep_ai_agent_instance',
      data: { id: aiAgentInstanceID },
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'sleep' },
  )
}

export function sendRenameAgent(aiAgentInstanceID: string, name: string) {
  sendMessage(
    {
      type: 'update_ai_agent_instance',
      data: { id: aiAgentInstanceID, name },
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'rename' },
  )
}

export function sendDeleteRule(ruleID: string) {
  sendMessage(
    { type: 'delete_rule', rule_id: ruleID },
    { kind: 'rule', id: ruleID, action: 'delete' },
  )
}

export function sendListRules() {
  sendMessage({ type: 'list_rules' }, { kind: 'rule', action: 'list' })
}

export function sendCompactRules(positionID: string, pattern: string) {
  sendMessage(
    {
      type: 'compact_rules',
      organization_position_id: positionID,
      pattern,
    },
    { kind: 'organization_position', id: positionID, action: 'compact_rules' },
  )
}

export function sendCreateRule(args: {
  positionID: string
  toolName: string
  pattern: string
  expiresAt?: string | null
}) {
  sendMessage(
    {
      type: 'create_rule',
      organization_position_id: args.positionID,
      tool_name: args.toolName,
      pattern: args.pattern,
      expires_at: args.expiresAt ?? null,
    },
    { kind: 'organization_position', id: args.positionID, action: 'create_rule' },
  )
}

export function sendCreateTempAgent(organizationID: string, hostID: string) {
  sendMessage(
    {
      type: 'create_temp_agent_instance',
      data: { organization_id: organizationID, host_id: hostID },
    },
    { kind: 'organization', id: organizationID, action: 'create_temp_agent' },
  )
}

export type TempAgentDefaults = {
  host_id?: string | null
  harness_id?: string | null
  ai_brain_model_id?: string | null
  harness_name?: string | null
}

export function fetchTempAgentDefaults(orgID: string): Promise<{ defaults: TempAgentDefaults; error?: string }> {
  return wsRequest(
    'get_temp_agent_defaults',
    { organization_id: orgID },
    'temp_agent_defaults',
    { kind: 'organization', id: orgID, action: 'get_temp_agent_defaults' },
  )
}

export function setTempAgentDefaults(
  orgID: string,
  defaults: TempAgentDefaults,
): Promise<{ defaults?: TempAgentDefaults; error?: string }> {
  return wsRequest(
    'set_temp_agent_defaults',
    { organization_id: orgID, ...defaults },
    'temp_agent_defaults_set',
    { kind: 'organization', id: orgID, action: 'set_temp_agent_defaults' },
  )
}

export function sendSetCurrentOrganization(organizationID: string) {
  // Server reads the target from msg.text (WSMessage has no
  // organization_id field on this code path — see main.go:2607).
  sendMessage(
    { type: 'set_current_organization', text: organizationID },
    { kind: 'organization', id: organizationID, action: 'set_current' },
  )
}

export type MyProSubscription = {
  id: string
  platform: string   // 'apple' | 'offer'
  product_id: string
  status: string
  purchased_at: string
  expires_at: string
}

export function sendListMyProSubscriptions(): Promise<{ subscriptions: MyProSubscription[] }> {
  return wsRequest('list_my_pro_subscriptions', {}, 'my_pro_subscriptions', {
    kind: 'subscription',
    action: 'list_my',
  })
}

export function sendRedeemOffer(code: string) {
  sendMessage(
    { type: 'redeem_offer', code },
    { kind: 'offer', id: code, action: 'redeem' },
  )
}

export function sendCheckOffer() {
  sendMessage({ type: 'check_offer' }, { kind: 'offer', action: 'check' })
}

// sendRelayFile sends a file from the user's phone to either an agent's working
// directory (pass agentInstanceID) or a chat room's working directory (pass
// roomID). The server resolves the path, forwards bytes to the daemon, and
// immediately acks with the on-host path. Rejects on error or after 10 s.
export function sendRelayFile(
  target: { agentInstanceID: string; roomID?: undefined } | { agentInstanceID?: undefined; roomID: string },
  filename: string,
  mimeType: string,
  dataB64: string,
): Promise<{ path: string }> {
  return new Promise((resolve, reject) => {
    let done = false
    const resolver = (msg: any) => {
      if (done) return
      done = true
      clearTimeout(timer)
      removeFromTypeQueue('relay_file_ack', resolver)
      if (msg.error) reject(new Error(msg.error as string))
      else resolve({ path: (msg.path as string) ?? '' })
    }
    const queue = pendingRequests.get('relay_file_ack') ?? []
    queue.push(resolver)
    pendingRequests.set('relay_file_ack', queue)
    const timer = setTimeout(() => {
      if (done) return
      done = true
      removeFromTypeQueue('relay_file_ack', resolver)
      reject(new Error('relay_file timed out'))
    }, 10_000)
    const payload: Record<string, unknown> = { type: 'relay_file', filename, mime_type: mimeType, data_b64: dataB64 }
    const action: Action = target.agentInstanceID
      ? { kind: 'ai_agent_instance', id: target.agentInstanceID, action: 'relay_file' }
      : { kind: 'organization', id: target.roomID!, action: 'relay_file' }
    if (target.agentInstanceID) payload.ai_agent_instance_id = target.agentInstanceID
    else payload.room_id = target.roomID
    sendMessage(payload, action)
  })
}

export function sendSubscribeAgent(aiAgentInstanceID: string) {
  if (!aiAgentInstanceID) return
  activeAgentSubscriptions.add(aiAgentInstanceID)
  sendMessage(
    {
      type: 'subscribe_agent',
      ai_agent_instance_id: aiAgentInstanceID,
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'subscribe' },
  )
}

export function sendUnsubscribeAgent(aiAgentInstanceID: string) {
  if (!aiAgentInstanceID) return
  activeAgentSubscriptions.delete(aiAgentInstanceID)
  sendMessage(
    {
      type: 'unsubscribe_agent',
      ai_agent_instance_id: aiAgentInstanceID,
    },
    { kind: 'ai_agent_instance', id: aiAgentInstanceID, action: 'unsubscribe' },
  )
}

// ---------- phase 2: Resource Connection CRUD ----------
//
// list/create/delete senders for the temporary CRUD view. Lists
// flow into the store via the *_list dispatcher branches above;
// create/delete go through wsRequest so the caller awaits a
// confirmation before refreshing the list. Pulled by the view's
// useEffect on mount + after every mutation.

// ---------- Hosts (debug CRUD: rename / forget) ----------
// Note: agent_home_path is write-once at enroll (HTTP endpoint
// /hosts/agent-home-dir, called from `hearth login`). No WS edit
// endpoint — to relocate, revoke + re-enroll.
// List rides on the 'hosts' push (replaceHosts on the
// store). Mutations go through wsRequest so the caller can await
// the confirmation before refreshing the list.

// ---------- Organization positions (debug CRUD: list / eliminate) ----------
// Positions have no per-position "name" field — display name comes from
// the joined JD title and is shared across every position using that JD.
// "Rename" therefore isn't a per-position concept and is omitted from
// this surface. Eliminate is soft-delete via eliminated_at; refuses if
// the position has live agents unless cascade=true (which retires them
// in the same transaction).

// ---------- Working directories (debug CRUD: list / edit path / abandon) ----------
// Path is a row's natural identity per host. Inline path edit goes
// through update_working_directory; server may reject on collision
// (a live row already owns the new path on the same host) — we surface
// the error and let the operator decide. Abandon cascades through to
// eliminate live positions + retire their agents when cascade=true.

// ---------- Organization members (debug CRUD: list / remove) ----------
// Remove is a hard delete on organization_human_users + cascades
// (cascadeMemberEvictionInOrg): retires the user's agents in the org,
// abandons their working directories, revokes their terminal devices.
// Surface this prominently in the confirm — there's no undo.

export function fetchOrganizationMembers(organizationID: string) {
  sendMessage(
    { type: 'list_organization_members', data: { organization_id: organizationID } },
    { kind: 'organization_members', action: 'list' },
  )
}

export function removeOrganizationMember(args: {
  organizationID: string
  humanUserID: string
}): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'remove_organization_member',
    { organization_id: args.organizationID, human_user_id: args.humanUserID },
    'organization_member_removed',
    { kind: 'organization_member', id: args.humanUserID, action: 'remove' },
  )
}

// Leave a household you're a member of. Needs nobody's approval (unlike
// removing yourself through remove_organization_member, which is household
// CRUD and asks the owner). An owner can't leave; they delete the household.
export function leaveHousehold(organizationID: string): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'leave_organization',
    { organization_id: organizationID },
    'organization_left',
    { kind: 'organization', id: organizationID, action: 'leave' },
  )
}

// Member-onboarding (§5.1): owner-guided setup of a newly-joined member.
// approve → make them an approver (owner-preserving, server-side); delegate →
// grant org-wide meta-edit; neither → just dismiss the prompt. All paths stamp
// governance_reviewed_at so the prompt won't reappear.
export function memberGovernanceSetup(args: {
  organizationID: string
  humanUserID: string
  approve?: boolean
  delegate?: boolean
}): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'member_governance_setup',
    {
      organization_id: args.organizationID,
      human_user_id: args.humanUserID,
      approve: !!args.approve,
      delegate: !!args.delegate,
    },
    'member_governance_setup_response',
    { kind: 'organization_member', id: args.humanUserID, action: 'governance_setup' },
  )
}

// ---------- Secrets (debug CRUD: list / delete / put) ----------
// Server scopes list by caller-owned hosts (no org filter). Delete
// removes the secret row AND cascade-deletes IAM rules referencing
// it. Put encrypts client-side to the host's X25519 pubkey and
// uploads the ciphertext envelope; the cleartext never crosses the
// wire.

export function fetchSecrets() {
  sendMessage(
    { type: 'secrets_list', data: {} },
    { kind: 'secrets', action: 'list' },
  )
}

export function deleteSecret(args: { id: string }) {
  // Server replies with secrets_delete_response on success or a generic
  // {type:"error"} on failure — fire-and-forget; caller re-lists.
  sendMessage(
    { type: 'secrets_delete', data: { id: args.id } },
    { kind: 'secret', id: args.id, action: 'delete' },
  )
}

export function fetchHostPubkey(
  hostID: string,
): Promise<{ type: string; host_id?: string; pubkey?: string; error?: string }> {
  return wsRequest(
    'host_pubkey_get',
    { host_id: hostID },
    'host_pubkey_response',
    { kind: 'host', id: hostID, action: 'pubkey_get' },
  )
}

export function putSecret(args: {
  hostID: string
  name: string
  ciphertextB64: string
  purpose?: string
  expiresAt?: string
}): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'secrets_put',
    {
      host_id: args.hostID,
      name: args.name,
      ciphertext: args.ciphertextB64,
      purpose: args.purpose ?? '',
      expires_at: args.expiresAt ? args.expiresAt : undefined,
    },
    'secrets_put_response',
    { kind: 'secret', id: args.name, action: 'put' },
  )
}

// ---------- Org invites (debug CRUD: list / revoke) ----------
// Status is server-derived from accepted_at / revoked_at / expires_at;
// revoke only succeeds on a pending row.

export function fetchInvites(organizationID: string) {
  sendMessage(
    { type: 'list_invites', data: { organization_id: organizationID } },
    { kind: 'invites', action: 'list' },
  )
}

export function revokeInvite(args: {
  id: string
}): Promise<{ type: string; error?: string; id?: string }> {
  return wsRequest(
    'revoke_invite',
    { id: args.id },
    'invite_revoked',
    { kind: 'invite', id: args.id, action: 'revoke' },
  )
}

// ---------- My invitations (invitee side) ----------
//
// Discovery is a lookup on the caller's verified email; there is no token
// anywhere in this section. The server also pushes `my_invites`
// unprompted on /ws connect and after every mutation below, so these
// helpers are for explicit refresh and for the mutations themselves.

/// Fire-and-forget refresh; the reply lands in the store as `myInvites`.
/// Use this for badges and counts that should track reality live.
export function fetchMyInvites() {
  sendMessage(
    { type: 'list_my_invites', data: {} },
    { kind: 'invites', action: 'list' },
  )
}

/// One-shot load that returns the list to the caller.
///
/// The invitations sheet uses this instead of subscribing to the store,
/// deliberately: it renders a multi-select, and a list that mutates under
/// a selection is a UI that changes what "Join" means between reading and
/// tapping. The sheet takes a snapshot, owns it, and updates it itself as
/// the user acts. Badges elsewhere stay live via fetchMyInvites.
export function requestMyInvites(): Promise<{
  type: string
  error?: string
  invites?: PendingInvite[]
}> {
  return wsRequest(
    'list_my_invites',
    {},
    'my_invites',
    { kind: 'invites', action: 'list' },
  )
}

/// Join one or many households in a single call. The server returns
/// `landing_organization_id` — the household the device was switched
/// into, which is the one whose invitation has been waiting longest.
/// Partial success is normal: `failed` maps invite_id → reason.
export function acceptInvites(args: {
  inviteIDs: string[]
  ioDeviceID: string
}): Promise<{
  type: string
  error?: string
  accepted?: { id: string; organization_id: string; organization_name: string }[]
  landing_organization_id?: string
  failed?: Record<string, string>
}> {
  return wsRequest(
    'accept_invites',
    { invite_ids: args.inviteIDs, io_device_id: args.ioDeviceID },
    'invites_accepted',
    { kind: 'invite', id: args.inviteIDs.join(','), action: 'accept' },
  )
}

/// Hide one invitation. The household may send another — blockOrgInvites
/// is the durable "stop asking".
export function declineInvite(args: {
  inviteID: string
}): Promise<{ type: string; error?: string; invite_id?: string }> {
  return wsRequest(
    'decline_invite',
    { invite_id: args.inviteID },
    'invite_declined',
    { kind: 'invite', id: args.inviteID, action: 'decline' },
  )
}

/// Mute a household: declines anything outstanding from it and stops
/// future invitations reaching this user. Not reported to the household.
export function blockOrgInvites(args: {
  organizationID: string
  blocked: boolean
}): Promise<{ type: string; error?: string; blocked?: boolean }> {
  return wsRequest(
    args.blocked ? 'block_org_invites' : 'unblock_org_invites',
    { organization_id: args.organizationID },
    'invite_block_updated',
    { kind: 'invite', id: args.organizationID, action: args.blocked ? 'block' : 'unblock' },
  )
}

// ---------- Agent job descriptions (debug CRUD: list / edit title / archive) ----------
// Archive soft-deletes via archived_at and cascades through every
// position using the JD (eliminating them + retiring their agents)
// when cascade=true. has_live_dependents response shape mirrors
// abandon_working_directory.

export function fetchAgentJobDescriptions(organizationID: string) {
  sendMessage(
    { type: 'list_agent_job_descriptions', data: { organization_id: organizationID } },
    { kind: 'agent_job_descriptions', action: 'list' },
  )
}

export function updateAgentJobDescription(args: {
  id: string
  title: string
  mandate?: string
  requiredSkills?: string
  priority?: number
}): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'update_agent_job_description',
    {
      id: args.id,
      title: args.title,
      mandate: args.mandate ?? '',
      required_skills: args.requiredSkills ?? '',
      priority: args.priority ?? 5,
    },
    'agent_job_description_updated',
    { kind: 'agent_job_description', id: args.id, action: 'update' },
  )
}

export function archiveAgentJobDescription(args: {
  id: string
  cascade?: boolean
}): Promise<{
  type: string
  error?: string
  blockers?: {
    positions?: Array<{ id: string; name: string; agents?: Array<{ id: string; name: string }> }>
  }
}> {
  return wsRequest(
    'archive_agent_job_description',
    { id: args.id, cascade: args.cascade ?? false },
    'agent_job_description_archived',
    { kind: 'agent_job_description', id: args.id, action: 'archive' },
  )
}

// --- Scheduled triggers ("Routines") -------------------------------------

// Fields shared by create + update. The server fills sensible defaults for the
// optional ones (enabled=true, overlap_policy='skip', catch_up=0).
export type ScheduledTriggerInput = {
  name: string
  enabled?: boolean
  cron_expr: string
  timezone: string
  target_mode: 'temp' | 'existing'
  target_ai_agent_instance_id?: string | null
  recipe_overrides_json?: string | null
  prompt: string
  catch_up_window_minutes?: number
  overlap_policy?: string
  // End conditions. RFC3339 instant; '' / 0 mean no bound. The client resolves
  // "the end of the 14th" against the Routine's own timezone and sends the
  // resulting instant, so the server never has to redo that calendar maths.
  ends_at?: string
  max_runs?: number
}

// Request the Routines list for a household. Arrives back as a
// `scheduled_triggers_list` push (routed in the switch below); screens call
// this on mount because the server only pushes on change.
export function listScheduledTriggers(organizationID: string) {
  sendMessage(
    { type: 'list_scheduled_triggers', data: { organization_id: organizationID } },
    { kind: 'scheduled_triggers', action: 'list' },
  )
}

// Fetch the onboarding review queue for a household. Arrives back as a
// `provisioning_proposals_list` frame (same handler as the live push). HomeScreen
// calls this on mount; the server pushes on change thereafter.
export function listProvisioningProposals(organizationID: string) {
  sendMessage(
    { type: 'provisioning_proposals_list', data: { organization_id: organizationID } },
    { kind: 'provisioning_proposals', action: 'list' },
  )
}

// Read the household's designated Onboarding Facilitator (the agent that reasons
// provisioning proposals). Empty ai_agent_instance_id = none designated.
// Mark a setup step done — the human-world half of a staged proposal
// (docs/blueprints.md §6). The verb is "I did this", not "retry": you are
// reporting a fact about the world, and what follows is the Facilitator's to
// work out.
export function completeProposalStep(
  organizationID: string,
  proposalID: string,
  itemID: string,
  note: string,
): Promise<{ remaining?: number; facilitator_tasked?: boolean; error?: string }> {
  return wsRequest(
    'provisioning_step_complete',
    { id: proposalID, item_id: itemID, note },
    'provisioning_step_completed',
    { kind: 'organization', id: organizationID, action: 'provisioning_step_complete' },
  )
}

// The published blueprint catalog — patterns other households already worked out
// (docs/blueprints.md). Listing is read-only; materializing is where the work is.
export function listBlueprints(
  organizationID: string,
): Promise<{
  catalog_version?: string
  blueprints?: Array<{
    slug: string
    version: string
    display_name: string
    summary: string
    tags?: string[]
  }>
  error?: string
}> {
  return wsRequest(
    'blueprints_list',
    { organization_id: organizationID },
    'blueprints_list',
    { kind: 'organization', id: organizationID, action: 'blueprints_list' },
  )
}

export type BlueprintNeed = {
  alias: string
  kind: string
  prompt: string
  guidance?: string
  setup?: string
  optional?: boolean
  options: Array<{ id: string; label: string }>
}

export type BlueprintParam = { name: string; prompt: string; default?: string }

// Turn a chosen blueprint into a reviewable proposal — or come back with the
// questions that stop it. Deliberately two-phase: the app answers and calls
// again, so a proposal is only ever created once every placeholder is bound.
export function materializeBlueprint(
  organizationID: string,
  blueprint: string,
  bindings: Record<string, string>,
  parameters: Record<string, string>,
): Promise<{
  needs?: BlueprintNeed[]
  parameters?: BlueprintParam[]
  provisioning_proposal?: { id: string }
  error?: string
}> {
  return wsRequest(
    'blueprint_materialize',
    { organization_id: organizationID, blueprint, bindings, parameters },
    'blueprint_materialized',
    { kind: 'organization', id: organizationID, action: 'blueprint_materialize' },
  )
}

// The household handbook: how things are done in this house
// (docs/introductions.md §3.2). Fetched on demand rather than pushed — it is
// content, potentially kilobytes, and has no business riding organizations_list.
export function getHouseholdHandbook(
  organizationID: string,
): Promise<{ handbook?: string; max_bytes?: number; error?: string }> {
  return wsRequest(
    'get_household_handbook',
    { organization_id: organizationID },
    'household_handbook',
    { kind: 'organization', id: organizationID, action: 'get_household_handbook' },
  )
}

export function setHouseholdHandbook(
  organizationID: string,
  handbook: string,
): Promise<{ handbook?: string; error?: string }> {
  return wsRequest(
    'set_household_handbook',
    { organization_id: organizationID, handbook },
    'household_handbook_set',
    { kind: 'organization', id: organizationID, action: 'set_household_handbook' },
  )
}

export function getOnboardingFacilitator(
  organizationID: string,
): Promise<{ ai_agent_instance_id?: string; agent_name?: string; error?: string }> {
  return wsRequest(
    'get_onboarding_facilitator',
    { organization_id: organizationID },
    'onboarding_facilitator',
    { kind: 'organization', id: organizationID, action: 'get_onboarding_facilitator' },
  )
}

// Designate (or clear, with '') the household's Onboarding Facilitator.
export function setOnboardingFacilitator(
  organizationID: string,
  aiAgentInstanceID: string,
): Promise<{ ai_agent_instance_id?: string; error?: string }> {
  return wsRequest(
    'set_onboarding_facilitator',
    { organization_id: organizationID, ai_agent_instance_id: aiAgentInstanceID },
    'onboarding_facilitator_set',
    { kind: 'organization', id: organizationID, action: 'set_onboarding_facilitator' },
  )
}

export function createScheduledTrigger(
  organizationID: string,
  input: ScheduledTriggerInput,
): Promise<{ type: string; error?: string; scheduled_trigger?: ScheduledTrigger }> {
  return wsRequest(
    'create_scheduled_trigger',
    { organization_id: organizationID, ...input },
    'scheduled_trigger_created',
    { kind: 'scheduled_trigger', action: 'create' },
  )
}

export function updateScheduledTrigger(
  id: string,
  input: ScheduledTriggerInput,
): Promise<{ type: string; error?: string; scheduled_trigger?: ScheduledTrigger }> {
  return wsRequest(
    'update_scheduled_trigger',
    { id, ...input },
    'scheduled_trigger_updated',
    { kind: 'scheduled_trigger', id, action: 'update' },
  )
}

export function archiveScheduledTrigger(
  id: string,
): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'archive_scheduled_trigger',
    { id },
    'scheduled_trigger_archived',
    { kind: 'scheduled_trigger', id, action: 'archive' },
  )
}

export function listScheduledTriggerRuns(
  scheduledTriggerID: string,
): Promise<{ type: string; error?: string; scheduled_trigger_runs?: ScheduledTriggerRun[] }> {
  return wsRequest(
    'list_scheduled_trigger_runs',
    { scheduled_trigger_id: scheduledTriggerID },
    'scheduled_trigger_runs',
    { kind: 'scheduled_trigger_runs', id: scheduledTriggerID, action: 'list' },
  )
}

export function fetchWorkingDirectories(organizationID: string) {
  sendMessage(
    { type: 'list_working_directories', data: { organization_id: organizationID } },
    { kind: 'working_directories', action: 'list' },
  )
}

export function updateWorkingDirectory(args: {
  id: string
  hostID: string
  directoryPath: string
}): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'update_working_directory',
    {
      id: args.id,
      host_id: args.hostID,
      directory_path: args.directoryPath,
    },
    'working_directory_updated',
    { kind: 'working_directory', id: args.id, action: 'update' },
  )
}

export function abandonWorkingDirectory(args: {
  id: string
  cascade?: boolean
}): Promise<{
  type: string
  error?: string
  blockers?: {
    positions?: Array<{ id: string; name: string; agents?: Array<{ id: string; name: string }> }>
  }
}> {
  return wsRequest(
    'abandon_working_directory',
    { id: args.id, cascade: args.cascade ?? false },
    'working_directory_abandoned',
    { kind: 'working_directory', id: args.id, action: 'abandon' },
  )
}

export function fetchOrganizationPositions(organizationID: string) {
  sendMessage(
    { type: 'list_organization_positions', data: { organization_id: organizationID } },
    { kind: 'organization_positions', action: 'list' },
  )
}

export function eliminateOrganizationPosition(args: {
  id: string
  cascade?: boolean
}): Promise<{
  type: string
  error?: string
  blockers?: { agents?: Array<{ id: string; name: string }> }
}> {
  return wsRequest(
    'eliminate_organization_position',
    { id: args.id, cascade: args.cascade ?? false },
    'organization_position_eliminated',
    { kind: 'organization_position', id: args.id, action: 'eliminate' },
  )
}

export function fetchHosts() {
  sendMessage({ type: 'list_hosts' }, { kind: 'hosts', action: 'list' })
}

export function renameHost(args: {
  hostID: string
  newHostname: string
}): Promise<{ type: string; host_id?: string; hostname?: string; error?: string }> {
  return wsRequest(
    'host_rename',
    { host_id: args.hostID, new_hostname: args.newHostname },
    'host_rename_response',
    { kind: 'host', id: args.hostID, action: 'rename' },
  )
}

// Change who approves this host's agents' tool calls (owner-only server-side).
// The server swaps the host-scoped approval_policies row atomically; a fresh
// hosts_list is pushed so the control reflects the new state.
export function setHostApprovalPolicy(args: {
  hostID: string
  policy: 'owner_only' | 'org_members'
}): Promise<{ type: string; host_id?: string; policy?: string; error?: string }> {
  return wsRequest(
    'set_host_approval_policy',
    { host_id: args.hostID, policy: args.policy },
    'host_approval_policy_set',
    { kind: 'host', id: args.hostID, action: 'set_approval_policy' },
  )
}

// ---------- IO devices (debug CRUD: list / rename / forget) ----------
// Owner-scoped list via list_my_io_devices (server returns 'my_io_devices'
// message which lands in store.ioDevices). Rename reuses the existing
// update_io_device handler — caller must send full struct (id, name,
// form_factor, owner, multi flags). Forget = soft-remove (removed_at),
// kicks the device's live WS so a phone/web session disconnects.

export function fetchMyIODevices() {
  sendMessage({ type: 'list_my_io_devices' },
    { kind: 'io_devices', action: 'list_my' })
}

// Household display screens enriched with serving host + what's-showing + health
// (§B6). Server replies with a 'display_screens_list' frame → store.displayScreens.
export function fetchDisplayScreens() {
  sendMessage({ type: 'list_display_screens' },
    { kind: 'display_screens', action: 'list' })
}

export function renameIODevice(args: {
  id: string
  newName: string
  formFactor: string
  ownerHumanUserID: string
  multiHumanUser: boolean
  multiUserOrganizationID?: string
}): Promise<{ type: string; io_device?: unknown; error?: string }> {
  return wsRequest(
    'update_io_device',
    {
      id: args.id,
      name: args.newName,
      form_factor: args.formFactor,
      owner_human_user_id: args.ownerHumanUserID,
      multi_human_user: args.multiHumanUser,
      organization_id: args.multiUserOrganizationID ?? '',
    },
    'io_device_updated',
    { kind: 'io_device', id: args.id, action: 'rename' },
  )
}

export function forgetIODevice(id: string): Promise<{
  type: string
  id?: string
  error?: string
}> {
  return wsRequest(
    'remove_io_device',
    { id },
    'io_device_removed',
    { kind: 'io_device', id, action: 'forget' },
  )
}

// Point a shared voice/display device at the organization_position whose agent
// it should drive (positionID '' clears the route). Owner-only, server-side —
// the target position must live in the device's owning household. See relay
// wsSetDeviceRoute.
export function setDeviceRoute(args: {
  ioDeviceID: string
  positionID: string
}): Promise<{
  type: string
  io_device_id?: string
  position_id?: string
  error?: string
}> {
  return wsRequest(
    'set_device_route',
    { io_device_id: args.ioDeviceID, position_id: args.positionID },
    'device_route_set',
    { kind: 'io_device', id: args.ioDeviceID, action: 'set_route' },
  )
}

export function forgetHost(hostID: string): Promise<{
  type: string
  host_id?: string
  revoked?: number
  error?: string
}> {
  return wsRequest(
    'host_forget',
    { host_id: hostID },
    'host_forget_response',
    { kind: 'host', id: hostID, action: 'forget' },
  )
}

// Personal-display agent grants: which household agents the owner has let know
// about / publish to one of their PERSONAL displays. Owner-only, server-guarded.
// Level: 'awareness' = the agent knows the screen exists and may query/reference
// it; 'access' = it may also publish; 'none' = nothing. See relay
// personal_display_grants.go.
export type PersonalDisplayLevel = 'none' | 'awareness' | 'access'

export interface PersonalDisplayGrant {
  agent_id: string
  agent_name: string
  position_id: string
  level: PersonalDisplayLevel
}

export function listPersonalDisplayGrants(screenID: string): Promise<{
  type: string
  screen_id?: string
  grants?: PersonalDisplayGrant[]
  error?: string
}> {
  return wsRequest(
    'list_personal_display_grants',
    { screen_id: screenID },
    'personal_display_grants',
    { kind: 'display', id: screenID, action: 'list_grants' },
  )
}

export function setPersonalDisplayGrant(args: {
  screenID: string
  positionID: string
  level: PersonalDisplayLevel
}): Promise<{
  type: string
  screen_id?: string
  position_id?: string
  level?: PersonalDisplayLevel
  ok?: boolean
  error?: string
}> {
  return wsRequest(
    'set_personal_display_grant',
    { screen_id: args.screenID, position_id: args.positionID, level: args.level },
    'personal_display_grant_set',
    { kind: 'display', id: args.screenID, action: 'set_grant' },
  )
}

export function fetchPluginInstalls() {
  sendMessage({ type: 'plugin_installs_list' },
    { kind: 'plugin_installs', action: 'list' })
}

// Ask a host to install (or reinstall/upgrade) a plugin from the catalog.
//
// The response only acknowledges that the request reached the host — the
// daemon then fetches from GitHub, verifies the catalog signature and every
// file hash against its own pinned key, and installs. The outcome arrives
// later as an unsolicited `plugin_install_result`; subscribe with
// subscribeOnce before calling, or a failure will be invisible.
export function requestPluginInstall(args: {
  hostID: string
  pluginSlug: string
  version?: string
  upgrade?: boolean
  force?: boolean
  // Consent to an update that removes verbs or adds required config.
  // Deliberately separate from `upgrade`: "replace this version" and "and I
  // accept that existing permission rules will stop matching" are different
  // decisions, and only the second one surprises people.
  allowBreaking?: boolean
}): Promise<{ status?: string; error?: string }> {
  return wsRequest(
    'plugin_install_request',
    {
      host_id: args.hostID,
      plugin_slug: args.pluginSlug,
      version: args.version,
      upgrade: args.upgrade,
      force: args.force,
      allow_breaking: args.allowBreaking,
    },
    'plugin_install_request_response',
    { kind: 'plugin_installs', id: args.hostID, action: 'install' },
  )
}

// PluginInstallResult is the daemon's outcome report, relayed to everyone in
// the household. Failure is the case that matters: a successful install shows
// up on its own when the plugin list refreshes, but a refusal leaves the list
// unchanged and is indistinguishable from "still working" without this.
export type PluginInstallResult = {
  host_id: string
  plugin_slug: string
  ok: boolean
  version?: string
  catalog_version?: string
  signature?: string
  error?: string

  // Set when the host REFUSED an update because it would remove verbs or add
  // required config. Not a failure in the usual sense — nothing is broken and
  // nothing was changed. It is a question, and the fields below are what the
  // answer should be based on.
  //
  // orphaned_rule_count and affected_agents are computed by the relay, which
  // owns the rules; the host only knows which verbs disappeared.
  breaking?: boolean
  verbs_removed?: string[]
  config_keys_now_required?: string[]
  orphaned_rule_count?: number
  affected_agents?: string[]
}

// Persistent, not one-shot. The result is broadcast to the whole household,
// so a given client can see several in a session — including ones it did not
// initiate. subscribeOnce would silently catch only the first and drop the
// rest, which for a failure channel means the errors that matter go missing.
const pluginInstallResultHandlers: Array<(m: PluginInstallResult) => void> = []

export function subscribePluginInstallResult(
  cb: (msg: PluginInstallResult) => void,
): () => void {
  pluginInstallResultHandlers.push(cb)
  return () => {
    const i = pluginInstallResultHandlers.indexOf(cb)
    if (i >= 0) pluginInstallResultHandlers.splice(i, 1)
  }
}

function emitPluginInstallResult(msg: PluginInstallResult) {
  pluginInstallResultHandlers.forEach((h) => h(msg))
}

export function fetchResourceConnections() {
  sendMessage({ type: 'resource_connections_list' },
    { kind: 'resource_connections', action: 'list' })
}

export function createResourceConnection(args: {
  slug: string
  pluginInstallID: string
  displayName?: string
  // JSON string. Plugin manifest's config_schema drives the form;
  // server validates against the same schema on write. Defaults to
  // '{}' server-side when omitted.
  config?: string
}): Promise<{ type: string; id?: string; slug?: string; error?: string }> {
  return wsRequest(
    'resource_connection_create',
    {
      slug: args.slug,
      plugin_install_id: args.pluginInstallID,
      display_name: args.displayName,
      config: args.config,
    },
    'resource_connection_create_response',
    { kind: 'resource_connection', id: args.slug, action: 'create' },
  )
}

export function updateResourceConnection(args: {
  id: string
  displayName?: string
  config?: string
}): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'resource_connection_update',
    { id: args.id, display_name: args.displayName, config: args.config },
    'resource_connection_update_response',
    { kind: 'resource_connection', id: args.id, action: 'update' },
  )
}

export function deleteResourceConnection(id: string): Promise<{
  type: string
  id?: string
  error?: string
}> {
  return wsRequest(
    'resource_connection_delete',
    { id },
    'resource_connection_delete_response',
    { kind: 'resource_connection', id, action: 'delete' },
  )
}

// ---------- Agent ↔ resource_connection grants (phase 5) ----------
// Per-agent grants live as IAM rule rows on the server; there's no
// separate join table. These wrap the agent_resource_grant_*
// handlers added in phase 2.

export type AgentResourceGrant = {
  resource_connection_id: string
  plugin_install_id: string
  host_id: string
  plugin_slug: string
  display_name?: string
}

export function fetchAgentResourceGrants(
  agentInstanceID: string,
): Promise<{
  type: string
  grants?: AgentResourceGrant[]
  error?: string
}> {
  return wsRequest(
    'agent_resource_grants_list',
    { ai_agent_instance_id: agentInstanceID },
    'agent_resource_grants_list',
    { kind: 'agent_resource_grants', id: agentInstanceID, action: 'list' },
  )
}

export function createAgentResourceGrant(args: {
  agentInstanceID: string
  resourceConnectionID: string
  rules: ManifestRuleApproval[]
}): Promise<{ type: string; seeded?: number; skipped?: number; error?: string }> {
  return wsRequest(
    'agent_resource_grant_create',
    {
      ai_agent_instance_id: args.agentInstanceID,
      resource_connection_id: args.resourceConnectionID,
      rules: args.rules,
    },
    'agent_resource_grant_create_response',
    { kind: 'agent_resource_grant', id: args.agentInstanceID, action: 'create' },
  )
}

// createAgentResourceAccess is the Surface-B write path (plan §8.2): one
// atomic call that writes rules + bindings for N agents at once. scope
// 'organization' authorizes every agent in the household (simple mode);
// 'agent' scopes the rules to just the named agents (advanced mode).
// Bindings are always minted for exactly the named agents.
export function createAgentResourceAccess(args: {
  resourceConnectionID: string
  aiAgentInstanceIDs: string[]
  rules: ManifestRuleApproval[]
  scope: 'organization' | 'agent'
}): Promise<{
  type: string
  scope?: string
  rules_written?: number
  bindings_written?: number
  agents?: string[]
  error?: string
}> {
  return wsRequest(
    'agent_resource_access_create',
    {
      resource_connection_id: args.resourceConnectionID,
      ai_agent_instance_ids: args.aiAgentInstanceIDs,
      rules: args.rules,
      scope: args.scope,
    },
    'agent_resource_access_create_response',
    { kind: 'agent_resource_grant', id: args.resourceConnectionID, action: 'create' },
  )
}

// ---------- Install wizard ----------
// Plugin manifest default_rules are suggestions; operators approve a
// subset through this wizard rather than the server auto-seeding at
// connection-create / grant-create time. See
// docs/iam-rule-types.md §"Plugin manifest default_rules are
// suggestions, not auto-grants".

// Shape returned by *_default_rules_get for each manifest rule.
// `decision` is the manifest's suggestion; `already_minted` flags
// rules with an existing equivalent row; `minted_decision` is what
// the existing row's decision is (so the UI can show "operator
// previously approved as 'allow' but manifest suggests 'ask'").
export type ManifestRuleWithStatus = {
  action: string
  decision: 'allow' | 'deny' | 'ask'
  when?: Record<string, unknown>
  already_minted: boolean
  minted_decision?: 'allow' | 'deny' | 'ask'
}

// Shape sent back to the approve endpoint. Predicate (`when`) comes
// from the manifest server-side; the operator only chooses which
// actions to approve and (optionally) overrides the decision.
export type ManifestRuleApproval = {
  action: string
  decision: 'allow' | 'deny' | 'ask'
}

// Asks the server whether the responding human has rule-minting
// authority for a pending permission_request — i.e. whether tapping
// "Always Allow" would actually mint a rule (true) or degrade to
// plain allow with no rule written (false). See
// docs/approver-resolution.md §"Rule-minting authority". The
// response sheet calls this before rendering the Always Allow button.
//
// can_deny is reserved for the future Always Deny button; v1 always
// returns false. Errors are reported in the `error` field; callers
// should default to NOT rendering the button on error (conservative).
export function fetchPermissionRequestMintingAuthority(requestID: string): Promise<{
  type: string
  request_id?: string
  can_allow?: boolean
  can_deny?: boolean
  error?: string
}> {
  return wsRequest(
    'permission_request_minting_authority_check',
    { request_id: requestID },
    'permission_request_minting_authority_check_response',
    { kind: 'permission_request', id: requestID, action: 'check_mint_authority' },
  )
}

export function fetchResourceConnectionDefaultRules(connectionID: string): Promise<{
  type: string
  resource_connection_id?: string
  manifest_defaults?: ManifestRuleWithStatus[]
  error?: string
}> {
  return wsRequest(
    'resource_connection_default_rules_get',
    { resource_connection_id: connectionID },
    'resource_connection_default_rules_get_response',
    { kind: 'resource_connection_default_rules', id: connectionID, action: 'get' },
  )
}

export function approveResourceConnectionDefaultRules(args: {
  resourceConnectionID: string
  rules: ManifestRuleApproval[]
}): Promise<{ type: string; seeded?: number; skipped?: number; error?: string }> {
  return wsRequest(
    'resource_connection_default_rules_approve',
    {
      resource_connection_id: args.resourceConnectionID,
      rules: args.rules,
    },
    'resource_connection_default_rules_approve_response',
    { kind: 'resource_connection_default_rules', id: args.resourceConnectionID, action: 'approve' },
  )
}

export function fetchAgentResourceGrantDefaultRules(args: {
  agentInstanceID: string
  resourceConnectionID: string
}): Promise<{
  type: string
  ai_agent_instance_id?: string
  resource_connection_id?: string
  manifest_defaults?: ManifestRuleWithStatus[]
  error?: string
}> {
  return wsRequest(
    'agent_resource_grant_default_rules_get',
    {
      ai_agent_instance_id: args.agentInstanceID,
      resource_connection_id: args.resourceConnectionID,
    },
    'agent_resource_grant_default_rules_get_response',
    { kind: 'agent_resource_grant_default_rules', id: args.agentInstanceID, action: 'get' },
  )
}

export function deleteAgentResourceGrant(args: {
  agentInstanceID: string
  resourceConnectionID: string
}): Promise<{ type: string; deleted?: number; error?: string }> {
  return wsRequest(
    'agent_resource_grant_delete',
    {
      ai_agent_instance_id: args.agentInstanceID,
      resource_connection_id: args.resourceConnectionID,
    },
    'agent_resource_grant_delete_response',
    { kind: 'agent_resource_grant', id: args.agentInstanceID, action: 'delete' },
  )
}

// ---------- Connection credentials ----------
// A connection authenticates as exactly one account. Its credential is a
// {name: secret_id} map on the connection row; a second upstream account is
// a second connection. (This replaced a connection_identities table that
// allowed N accounts per connection but could never let one agent use two
// of them — bindings are UNIQUE(agent, connection).)

import type { AgentResourceBinding as _AgentResourceBinding } from '../store/types'

// setConnectionCredential files the secret a connection authenticates with,
// for plugins whose credential is pasted rather than obtained via OAuth
// (Home Assistant tokens, GitHub PATs, service-account JSON). OAuth
// connections never call this — begin_oauth's callback files the credential
// itself, atomically with the token exchange.
//
// Omit `name` to use the plugin manifest's first declared credential, which
// is the whole set for every shipped manifest. Pass an empty secretID to
// disconnect (clears the reference; the secret row itself survives, since
// one secret can back several connections).
export function setConnectionCredential(args: {
  resourceConnectionID: string
  secretID: string
  name?: string
}): Promise<{
  type: string
  resource_connection_id?: string
  name?: string
  error?: string
}> {
  return wsRequest(
    'set_connection_credential',
    {
      resource_connection_id: args.resourceConnectionID,
      secret_id: args.secretID,
      name: args.name,
    },
    'set_connection_credential_response',
    {
      kind: 'resource_connection',
      id: args.resourceConnectionID,
      action: args.secretID ? 'authorize' : 'disconnect',
    },
  )
}

// ---------- OAuth dance ----------

// beginOAuth authorizes ONE connection. Running it against a connection
// that already holds a credential re-authorizes it in place: the callback
// rewrites the same secret's ciphertext, so agent bindings and their
// secret.use rules survive untouched. There is no "connect another account"
// — that is a second connection.
export function beginOAuth(args: {
  resourceConnectionID: string
  hostID: string
  provider: string
  label: string
}): Promise<{ type: string; intent_id?: string; auth_url?: string; error?: string }> {
  return wsRequest(
    'begin_oauth',
    {
      resource_connection_id: args.resourceConnectionID,
      host_id: args.hostID,
      provider: args.provider,
      label: args.label,
    },
    'begin_oauth_response',
    { kind: 'resource_connection', id: args.resourceConnectionID, action: 'authorize' },
  )
}

// subscribeConnectionAuthorized registers a one-shot listener for the
// connection_authorized WS event the server broadcasts when an OAuth dance
// completes. Returns an unsubscribe function.
export function subscribeConnectionAuthorized(
  cb: (msg: { intent_id: string; resource_connection_id: string }) => void,
): () => void {
  console.log('[oauth] subscribeConnectionAuthorized: registered')
  return subscribeOnce('connection_authorized', (msg) => {
    console.log('[oauth] connection_authorized received:', msg)
    cb(msg)
  })
}

// ---------- Agent resource bindings (phase 3) ----------
// Bindings are "this agent uses this variant of this connection" —
// distinct from agent_resource_grants (which is IAM policy). Created
// proactively from Agent Detail's Add resource flow, or reactively
// via a pick permission_request (pick_kind='resource_binding') when
// an agent invokes without a binding.

export function fetchAgentResourceBindings(
  agentInstanceID: string,
): Promise<{
  type: string
  agent_resource_bindings?: _AgentResourceBinding[]
  ai_agent_instance_id?: string
  error?: string
}> {
  return wsRequest(
    'agent_resource_bindings_list',
    { ai_agent_instance_id: agentInstanceID },
    'agent_resource_bindings_list',
    {
      kind: 'agent_resource_bindings',
      id: agentInstanceID,
      action: 'list',
    },
  )
}

export function createAgentResourceBinding(args: {
  agentInstanceID: string
  resourceConnectionID: string
  stateDirPath?: string
}): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'agent_resource_binding_create',
    {
      ai_agent_instance_id: args.agentInstanceID,
      resource_connection_id: args.resourceConnectionID,
      state_dir_path: args.stateDirPath,
    },
    'agent_resource_binding_create_response',
    {
      kind: 'agent_resource_binding',
      id: args.agentInstanceID,
      action: 'create',
    },
  )
}

export function revokeAgentResourceBinding(
  id: string,
): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'agent_resource_binding_revoke',
    { id },
    'agent_resource_binding_revoke_response',
    { kind: 'agent_resource_binding', id, action: 'revoke' },
  )
}

export function cycleAgentInstance(
  agentInstanceID: string,
): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'cycle_agent_instance',
    { ai_agent_instance_id: agentInstanceID },
    'cycle_agent_instance_response',
    { kind: 'agent_instance', id: agentInstanceID, action: 'cycle' },
  )
}

// ---------- Rules debug CRUD ----------
// Generic CRUD over the rules table. Backs the "Rules" debug sheet.
// Distinct from the position-tool list_rules / create_rule /
// delete_rule path (that one is GrantsSegment-shaped and reshapes
// arguments). See cmd/hearth-cloud/rules_admin_ws.go.

import type { Rule as _Rule } from '../store/types'

export function fetchRulesAdmin(): Promise<{
  type: string
  rules?: _Rule[]
  error?: string
}> {
  return wsRequest('rules_admin_list', {}, 'rules_admin_list', {
    kind: 'rules_admin',
    id: 'all',
    action: 'list',
  })
}

export function createRuleAdmin(args: {
  principalScopeKind: string
  principalScopeValue: string
  resourceKind: string
  resourceID?: string | null
  action: string
  predicate?: string | null
  decision: string
  expiresAt?: string | null
}): Promise<{ type: string; id?: number; error?: string }> {
  return wsRequest(
    'rule_admin_create',
    {
      principal_scope_kind: args.principalScopeKind,
      principal_scope_value: args.principalScopeValue,
      resource_kind: args.resourceKind,
      resource_id: args.resourceID ?? null,
      action: args.action,
      predicate: args.predicate ?? null,
      decision: args.decision,
      expires_at: args.expiresAt ?? null,
    },
    'rule_admin_create_response',
    { kind: 'rules_admin', id: args.action, action: 'create' },
  )
}

export function deleteRuleAdmin(
  id: string,
): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'rule_admin_delete',
    { id },
    'rule_admin_delete_response',
    { kind: 'rules_admin', id, action: 'delete' },
  )
}

// ---------- Per-device notification preferences ----------
// Backs the "Notification Preferences" sheet under About. Per-device
// opt-out (composed AND with the user-level notification_preferences
// in the legacy table) — a user can mute one device while keeping
// pushes alive on their others. See
// cmd/hearth-cloud/device_notification_prefs_ws.go.

export type DeviceNotificationPreferences = {
  permission_requests: boolean
  mentions: boolean
  household_activity: boolean
  announcements: boolean
}

export function fetchDeviceNotificationPreferences(): Promise<{
  type: string
  device_id?: string
  preferences?: DeviceNotificationPreferences
  error?: string
}> {
  return wsRequest(
    'device_notification_preferences_get',
    {},
    'device_notification_preferences',
    { kind: 'device_notification_preferences', id: 'self', action: 'get' },
  )
}

export function setDeviceNotificationPreference(args: {
  category: keyof DeviceNotificationPreferences
  enabled: boolean
}): Promise<{
  type: string
  device_id?: string
  preferences?: DeviceNotificationPreferences
  error?: string
}> {
  return wsRequest(
    'device_notification_preferences_set',
    { category: args.category, enabled: args.enabled },
    'device_notification_preferences',
    { kind: 'device_notification_preferences', id: args.category, action: 'set' },
  )
}

// ---------- Pending approvals (inbox) ----------
// Backs the Attention inbox's approvals and the badge refresh. Returns
// every in-flight permission_request where the calling human is in
// the resolver's approver set — across all orgs/agents, including
// agents the device isn't currently subscribed to. Entries shape
// matches the live `permission_request` frame so the same
// normalizePermissionRequest + PermissionRequestCard pipeline is
// reusable. See cmd/hearth-cloud/pending_approvals_ws.go and
// project_approver_inbox_todo.md.

type PendingApprovalEntry = {
  request_id: string
  tool_name: string
  tool_input?: Record<string, unknown>
  subject_kind?: string
  subject?: unknown
  ai_agent_instance_id?: string
  organization_position_id?: string
  agent_name?: string
  position_name?: string
  organization_name?: string
  organization_id?: string
  source_label?: string
  outside_project?: boolean
  timeout: number
  parked?: boolean
  created_at: string
}

// Advance this human's read watermark for a room, so it stops counting toward
// the app-icon badge (and its pip clears on this device and any other).
//
// `upTo` is the timestamp the client has actually SEEN, not "now": sending now
// would mark read any message that arrived while the screen was mounting,
// which is the single moment a message is most likely to be missed. Omit it
// only when there is nothing loaded to point at.
//
// The response carries the recipient's remaining unread-room count, so the
// badge settles the moment a room is opened rather than waiting for the next
// push.
// A thread's first message plus a page of its replies (oldest first). beforeID
// pages back through older replies.
export function sendListChatThread(
  rootID: string,
  beforeID?: string,
): Promise<{
  type: string
  room_id?: string
  root?: unknown
  messages?: unknown[]
  has_more?: boolean
  // Whether the viewer is notified about this thread's replies.
  following?: boolean
  error?: string
}> {
  return wsRequest(
    'list_chat_thread',
    beforeID ? { root_id: rootID, before_id: beforeID } : { root_id: rootID },
    'chat_thread_messages',
    { kind: 'chat_message', id: rootID, action: 'list_thread' },
  )
}

// "Also send to #room" after the fact: one of your own thread replies that was
// posted thread-only now shows in the room too. Throws if the server refused.
export async function sendChatReplyToChannel(messageID: string): Promise<void> {
  const r = (await wsRequest(
    'send_chat_reply_to_channel',
    { message_id: messageID },
    'chat_reply_sent_to_channel',
    { kind: 'chat_message', id: messageID, action: 'send_reply_to_channel' },
  )) as { error?: string } | undefined
  if (r?.error) throw new Error(r.error)
}

// Turn notifications for a thread's replies off (following=false) or back on.
// Off: no pushes for its replies, and it stops counting toward the badge and
// the inbox. Replying or being @mentioned turns it back on (server rule).
// Resolves to the new state; throws if the server refused.
export async function sendSetChatThreadFollow(rootID: string, following: boolean): Promise<boolean> {
  const r = (await wsRequest(
    'set_chat_thread_follow',
    { root_id: rootID, following },
    'chat_thread_follow',
    { kind: 'chat_message', id: rootID, action: 'set_thread_follow' },
  )) as { following?: boolean; unread_thread_count?: number; error?: string } | undefined
  if (r?.error) throw new Error(r.error)
  const s = useAppStore.getState()
  if (typeof r?.unread_thread_count === 'number') {
    s.setUnreadThreadCount(r.unread_thread_count)
    reconcileNotifications()
  }
  // A thread you stopped following leaves the inbox.
  if (!following && s.attentionThreads.some((t) => t.root_id === rootID)) {
    s.setAttentionThreads(s.attentionThreads.filter((t) => t.root_id !== rootID))
  }
  return r?.following ?? following
}

// Records how far the viewer has read a thread. Opening a thread does not make
// you follow it. Best-effort, like marking a room read.
export async function sendMarkChatThreadRead(rootID: string, upTo?: string): Promise<void> {
  const s = useAppStore.getState()
  // Reading it takes it off the inbox right away.
  if (s.attentionThreads.some((t) => t.root_id === rootID)) {
    s.setAttentionThreads(s.attentionThreads.filter((t) => t.root_id !== rootID))
  }
  try {
    const r = (await wsRequest(
      'mark_chat_thread_read',
      upTo ? { root_id: rootID, up_to: upTo } : { root_id: rootID },
      'chat_thread_read',
      { kind: 'chat_message', id: rootID, action: 'mark_thread_read' },
    )) as { unread_thread_count?: number } | undefined
    // Settle the badge the moment the thread is read.
    if (typeof r?.unread_thread_count === 'number') {
      useAppStore.getState().setUnreadThreadCount(r.unread_thread_count)
      reconcileNotifications()
    }
  } catch {
    // Best-effort; the next open corrects it.
  }
}

export async function sendMarkChatRoomRead(roomID: string, upTo?: string): Promise<void> {
  try {
    const r = (await wsRequest(
      'mark_chat_room_read',
      upTo ? { room_id: roomID, up_to: upTo } : { room_id: roomID },
      'chat_room_read',
      { kind: 'chat_room', id: roomID, action: 'mark_read' },
    )) as
      | { unread_room_count?: number; unread_mention_count?: number; unread_thread_count?: number }
      | undefined
    const count = r?.unread_room_count ?? r?.unread_mention_count
    if (typeof r?.unread_thread_count === 'number') {
      useAppStore.getState().setUnreadThreadCount(r.unread_thread_count)
    }
    if (typeof count === 'number') {
      useAppStore.getState().setUnreadRoomCount(count)
      reconcileNotifications()
    }
  } catch {
    // Best-effort. A failed mark-read leaves the badge lit a little longer;
    // the next open or refresh corrects it. Never worth surfacing.
  }
}

export function fetchPendingApprovals(): Promise<{
  type: string
  entries?: PendingApprovalEntry[]
  error?: string
  // The chat half of the app-icon badge — number of rooms with unread.
  // unread_mention_count is the pre-2026-09-15 field, kept for a mid-deploy
  // beat so an older server's response still yields a number.
  unread_room_count?: number
  unread_mention_count?: number
}> {
  return wsRequest('list_pending_approvals', {}, 'pending_approvals_list', {
    kind: 'pending_approvals',
    id: 'self',
    action: 'list',
  })
}

// Refresh the per-household attention counts (asks + unread rooms) that feed the
// household switcher's chips. Best-effort: on failure the chips keep their last
// known values rather than flashing to zero. Called on connect, on foreground,
// and when the switcher opens — the moments a person would look at it.
export async function fetchAttention(): Promise<void> {
  try {
    const r = (await wsRequest('list_attention', {}, 'attention_list', {
      kind: 'attention',
      id: 'self',
      action: 'list',
    })) as
      | {
          households?: {
            organization_id: string
            asks?: number
            unread_rooms?: number
            unread_threads?: number
            suggestions?: number
          }[]
        }
      | undefined
    const map: Record<string, HouseholdAttention> = {}
    for (const h of r?.households ?? []) {
      if (!h.organization_id) continue
      map[h.organization_id] = {
        asks: h.asks ?? 0,
        unread_rooms: h.unread_rooms ?? 0,
        unread_threads: h.unread_threads ?? 0,
        suggestions: h.suggestions ?? 0,
      }
    }
    useAppStore.getState().setAttentionByOrg(map)
  } catch {
    // best-effort; leave the last known counts in place
  }
}

// Load the cross-household unread-rooms list that the Attention Inbox renders as
// chat rows. Heavier than the chip counts, so it's fetched only when the inbox
// opens rather than on every connect.
export async function fetchAttentionRooms(): Promise<void> {
  try {
    const r = (await wsRequest('list_attention_rooms', {}, 'attention_rooms', {
      kind: 'attention',
      id: 'self',
      action: 'list_rooms',
    })) as
      | {
          rooms?: import('../store/types').AttentionRoom[]
          threads?: import('../store/types').AttentionThread[]
          suggestions?: import('../store/types').AttentionSuggestion[]
        }
      | undefined
    useAppStore.getState().setAttentionRooms(r?.rooms ?? [])
    useAppStore.getState().setAttentionThreads(r?.threads ?? [])
    useAppStore.getState().setAttentionSuggestions(r?.suggestions ?? [])
  } catch {
    // best-effort; the inbox shows whatever it last had
  }
}

// Open a chat room from the Attention Inbox: switch to its household first (the
// room may be in a household this device isn't currently viewing), then push
// the room screen. Mirrors the push.tapped room branch — the room screen loads
// its own messages by membership, so navigation is safe before the switch
// lands, and opening it advances the read watermark, clearing the unread.
export function openAttentionChatRoom(
  organizationID: string,
  roomID: string,
  roomName: string,
): void {
  const s = useAppStore.getState()
  switchOrganizationForPush(organizationID)
  const alreadyOpen = s.navStack.some((e) => e.kind === 'chat_room' && e.roomID === roomID)
  if (!alreadyOpen) s.pushNav({ kind: 'chat_room', roomID, roomName: roomName || 'Chat' })
}

// Open a setup suggestion from the inbox: switch to its household, then its
// review screen (which loads the proposal by id, so it works before the
// household's own lists arrive).
export function openAttentionSuggestion(organizationID: string, proposalID: string): void {
  switchOrganizationForPush(organizationID)
  useAppStore.getState().pushNav({ kind: 'onboarding_review', proposalID })
}

// Open a thread from outside its room (the inbox, a notification tap): the room
// first, then the thread over it, so Back lands in the room as it does when you
// open a thread from the room yourself. Switches household first.
export function openChatThread(
  organizationID: string | undefined,
  roomID: string,
  roomName: string,
  rootID: string,
): void {
  switchOrganizationForPush(organizationID)
  const s = useAppStore.getState()
  const top = s.navStack[s.navStack.length - 1]
  if (top?.kind === 'chat_thread' && top.rootID === rootID) return
  const name = roomName || 'Chat'
  if (!(top?.kind === 'chat_room' && top.roomID === roomID)) {
    s.pushNav({ kind: 'chat_room', roomID, roomName: name })
  }
  s.pushNav({ kind: 'chat_thread', roomID, roomName: name, rootID })
}

// Convert a raw entry into the PermissionRequest shape consumers
// expect. Reuses normalizePermissionRequest (module-internal) by
// shaping the entry to look like a permission_request frame.
export function normalizePendingApprovalEntry(
  e: PendingApprovalEntry,
): import('../store/types').PermissionRequest {
  return normalizePermissionRequest({
    request_id: e.request_id,
    tool_name: e.tool_name,
    tool_input: e.tool_input,
    subject_kind: e.subject_kind,
    subject: e.subject,
    ai_agent_instance_id: e.ai_agent_instance_id,
    organization_position_id: e.organization_position_id,
    agent_name: e.agent_name,
    position_name: e.position_name,
    organization_name: e.organization_name,
    organization_id: e.organization_id,
    outside_project: e.outside_project,
    timeout: e.timeout,
    parked: e.parked,
  })
}

// ---------- Open requests (axis-D pull surface, X2) ----------

// One entry from list_open_requests: a pending request anywhere in the viewed
// household, annotated per-caller with can_act (∈ B — may act, even off-ladder)
// and actionable_now (their rung is live — the dynamic inbox). Superset of
// PendingApprovalEntry with the pull-surface fields.
export type OpenRequestEntry = PendingApprovalEntry & {
  domain: string
  gate_action?: string
  can_act: boolean
  actionable_now: boolean
  initiated_by_me?: boolean
}

// fetchOpenRequests reads the browsable list of every open request in the
// household the device is viewing (reads-open). Unlike fetchPendingApprovals
// (only what I've been paged for), this returns items I can browse and, where
// can_act is true, act on off-ladder.
export function fetchOpenRequests(): Promise<{
  type: string
  entries?: OpenRequestEntry[]
  error?: string
}> {
  return wsRequest('list_open_requests', {}, 'open_requests_list', {
    kind: 'open_requests',
    id: 'self',
    action: 'list',
  })
}

// rehydrateInitiatorNotices makes the initiator banner (X2) DURABLE across
// reloads and app-close/reopen (§2.6: pending state must be observable, not
// swallowed). The live permission_pending frames are lost when the socket was
// down, so on every (re)connect we pull the open list and re-seed a pending
// banner for each request THIS user initiated that's still open. Idempotent:
// upsert is keyed by request_id and a live richer notice already present is
// left untouched. Best-effort — a failed pull just leaves the transient state.
// seedInitiatorNoticesFromOpenRequests re-seeds a pending banner for each open
// request THIS user initiated. Pure over its input (no socket) so it's unit
// testable. Idempotent: skips a request that already has a notice, so it never
// clobbers a live richer body with the rehydrated placeholder.
export function seedInitiatorNoticesFromOpenRequests(entries: OpenRequestEntry[]): void {
  const s = useAppStore.getState()
  for (const e of entries) {
    if (!e.initiated_by_me) continue
    if (s.initiatorNotices.some((n) => n.requestId === e.request_id)) continue
    const label = e.tool_name || 'your request'
    s.upsertInitiatorNotice({
      requestId: e.request_id,
      tool: e.tool_name ?? '',
      status: 'pending',
      body: `Pending — waiting for approval of ${label}.`,
      ts: e.created_at ? Date.parse(e.created_at) : Date.now(),
    })
  }
}

export async function rehydrateInitiatorNotices(): Promise<void> {
  try {
    const res = await fetchOpenRequests()
    seedInitiatorNoticesFromOpenRequests(res.entries ?? [])
  } catch {
    // best-effort; the WS frames remain the primary path
  }
}

// normalizeOpenRequestEntry shapes an entry into the PermissionRequest the
// act-flow card consumes. The browse-only fields (domain/can_act/actionable_now)
// stay on the raw entry; only entries the caller acts on are normalized.
export function normalizeOpenRequestEntry(
  e: OpenRequestEntry,
): import('../store/types').PermissionRequest {
  return normalizePendingApprovalEntry(e)
}

// ---------- Per-user (cross-device) notification preferences ----------
// Sibling to the per-device prefs above. Only covers categories that
// aren't agent-scoped (household_activity, announcements) — those are
// not handled by the legacy per-agent notification_preferences. AND-
// composes with the per-device toggles: a push delivers to a device
// only when BOTH user-level and device-level prefs admit. See
// cmd/hearth-cloud/user_notification_prefs_ws.go.

export type UserNotificationPreferences = {
  household_activity: boolean
  announcements: boolean
}

export function fetchUserNotificationPreferences(): Promise<{
  type: string
  preferences?: UserNotificationPreferences
  error?: string
}> {
  return wsRequest(
    'user_notification_preferences_get',
    {},
    'user_notification_preferences',
    { kind: 'user_notification_preferences', id: 'self', action: 'get' },
  )
}

export function setUserNotificationPreference(args: {
  category: keyof UserNotificationPreferences
  enabled: boolean
}): Promise<{
  type: string
  preferences?: UserNotificationPreferences
  error?: string
}> {
  return wsRequest(
    'user_notification_preferences_set',
    { category: args.category, enabled: args.enabled },
    'user_notification_preferences',
    { kind: 'user_notification_preferences', id: args.category, action: 'set' },
  )
}

// ---------- Approver-resolution policies ----------
// CRUD over approval_policies rows. Backs the Approvers screen
// under household settings. See cmd/hearth-cloud/approval_policies_ws.go
// and docs/approver-resolution.md §"Editor / UX surface".

export type ApprovalPolicyRow = {
  id: string
  resource_kind: string
  resource_id: string
  action_pattern: string
  approver_kind: 'human' | 'agent' | 'position' | 'org_member'
  approver_id: string
  approver_label: string
  created_at: string
}

export function fetchApprovalPolicies(): Promise<{
  type: string
  rows?: ApprovalPolicyRow[]
  error?: string
}> {
  return wsRequest('approval_policies_list', {}, 'approval_policies_list', {
    kind: 'approval_policies',
    id: 'all',
    action: 'list',
  })
}

export function createApprovalPolicy(args: {
  resourceKind?: string
  resourceID?: string
  actionPattern?: string
  approverKind: 'human' | 'agent' | 'position' | 'org_member'
  approverID: string
}): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'approval_policy_create',
    {
      resource_kind: args.resourceKind ?? '*',
      resource_id: args.resourceID ?? '*',
      action_pattern: args.actionPattern ?? '*',
      approver_kind: args.approverKind,
      approver_id: args.approverID,
    },
    'approval_policy_create_response',
    { kind: 'approval_policies', id: args.approverID, action: 'create' },
  )
}

export function deleteApprovalPolicy(
  id: string,
): Promise<{ type: string; ok?: boolean; error?: string }> {
  return wsRequest(
    'approval_policy_delete',
    { id },
    'approval_policy_delete_response',
    { kind: 'approval_policies', id, action: 'delete' },
  )
}

// ---- Delegations (axis-C write path, L1) ---------------------------------
// A delegation grants a housemate authority to edit a resource's approval /
// rule / escalation policy. Create is OWNER-ONLY (C2); delete is owner or the
// delegate themselves; list is reads-open. See
// cmd/hearth-cloud/policy_delegations_ws.go and docs/authz-policy-model.md §3.4.

export type PolicyDelegationRow = {
  id: string
  resource_kind: string
  resource_id: string
  delegate_human_user_id: string
  delegate_label: string
  granted_by_human_user_id: string
  granted_by_label: string
  can_subdelegate: boolean
  created_at: string
  expires_at?: string
}

export function fetchPolicyDelegations(): Promise<{
  type: string
  rows?: PolicyDelegationRow[]
  error?: string
}> {
  return wsRequest('policy_delegations_list', {}, 'policy_delegations_list', {
    kind: 'policy_delegations',
    id: 'all',
    action: 'list',
  })
}

export function createPolicyDelegation(args: {
  resourceKind: string
  resourceID: string
  delegateHumanID: string
  expiresAt?: string // RFC3339; omit for permanent
}): Promise<{ type: string; id?: string; error?: string }> {
  return wsRequest(
    'policy_delegation_create',
    {
      resource_kind: args.resourceKind,
      resource_id: args.resourceID,
      delegate_human_user_id: args.delegateHumanID,
      ...(args.expiresAt ? { expires_at: args.expiresAt } : {}),
    },
    'policy_delegation_create_response',
    { kind: 'policy_delegations', id: args.delegateHumanID, action: 'create' },
  )
}

export function deletePolicyDelegation(
  id: string,
): Promise<{ type: string; ok?: boolean; error?: string }> {
  return wsRequest(
    'policy_delegation_delete',
    { id },
    'policy_delegation_delete_response',
    { kind: 'policy_delegations', id, action: 'delete' },
  )
}

// ---- Org Chat ------------------------------------------------------------

export function fetchChatNotifPrefs(
  roomID: string,
): Promise<{ type: string; room_id?: string; mode?: string; error?: string }> {
  return wsRequest(
    'get_chat_notification_prefs',
    { room_id: roomID },
    'chat_notification_prefs',
    { kind: 'chat_room', id: roomID, action: 'get_notif_prefs' },
  )
}

export function setChatNotifPrefs(
  roomID: string,
  mode: string,
): Promise<{ type: string; room_id?: string; mode?: string; error?: string }> {
  return wsRequest(
    'set_chat_notification_prefs',
    { room_id: roomID, mode },
    'chat_notification_prefs',
    { kind: 'chat_room', id: roomID, action: 'set_notif_prefs' },
  )
}

export function sendListChatRooms(organizationID: string) {
  sendMessage(
    { type: 'list_chat_rooms' },
    { kind: 'organization', id: organizationID, action: 'list_chat_rooms' },
  )
}

export function sendListChatMessages(
  roomID: string,
  beforeID?: string,
): Promise<{ type: string; messages?: unknown[]; has_more?: boolean; error?: string }> {
  return wsRequest(
    'list_chat_messages',
    beforeID ? { room_id: roomID, before_id: beforeID } : { room_id: roomID },
    'chat_messages_list',
    { kind: 'chat_room', id: roomID, action: 'list_messages' },
  )
}

export async function sendChatMessage(
  roomID: string,
  text: string,
  replyToID?: string | null,
  attachmentIDs?: string[],
  // A reply in a thread: the thread's first message, and whether to also show
  // the reply in the channel.
  thread?: { rootID: string; alsoInChannel?: boolean } | null,
): Promise<{ type: string; message?: unknown; error?: string }> {
  const res = await wsRequest<{ type: string; message?: unknown; error?: string }>(
    'send_chat_message',
    {
      room_id: roomID,
      text,
      ...(replyToID ? { reply_to_id: replyToID } : {}),
      ...(attachmentIDs && attachmentIDs.length ? { attachment_ids: attachmentIDs } : {}),
      ...(thread
        ? { thread_root_id: thread.rootID, ...(thread.alsoInChannel ? { also_in_channel: true } : {}) }
        : {}),
    },
    'chat_message_sent',
    { kind: 'chat_room', id: roomID, action: 'send_message' },
  )
  // The relay reports a rejected send (room not found, forbidden, DB error, bad
  // payload) as a RESOLVED response carrying `error` — not a transport failure.
  // Promote it to a throw so every caller's catch handles server rejection and
  // transport drop the same way; the alternative is a resolve that a caller
  // forgets to inspect, which is exactly how a send used to vanish silently.
  if (res.error) throw new Error(res.error)
  return res
}

// uploadChatAttachment stores a file server-side and returns its metadata. This
// is the first half of the two-step post (upload → then sendChatMessage with the
// returned id). The bytes persist on the server; the message links them on send.
export function uploadChatAttachment(
  roomID: string,
  filename: string,
  mimeType: string,
  dataB64: string,
): Promise<{ attachment?: _ChatAttachment; error?: string }> {
  return wsRequest(
    'upload_chat_attachment',
    { room_id: roomID, filename, mime_type: mimeType, data_b64: dataB64 },
    'chat_attachment_uploaded',
    { kind: 'chat_room', id: roomID, action: 'upload_attachment' },
    { timeoutMs: 30_000 },
  )
}
import type { ChatAttachment as _ChatAttachment } from '../store/types'

export function deleteChatMessage(
  messageID: string,
): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'delete_chat_message',
    { message_id: messageID },
    'chat_message_deleted',
    { kind: 'chat_message', id: messageID, action: 'delete' },
  )
}

export function editChatMessage(
  messageID: string,
  text: string,
): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'edit_chat_message',
    { message_id: messageID, text },
    'chat_message_edited',
    { kind: 'chat_message', id: messageID, action: 'edit' },
  )
}

export function addChatReaction(
  messageID: string,
  emoji: string,
): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'add_chat_reaction',
    { message_id: messageID, emoji },
    'chat_reaction_update',
    { kind: 'chat_message', id: messageID, action: 'add_reaction' },
  )
}

export function removeChatReaction(
  messageID: string,
  emoji: string,
): Promise<{ type: string; error?: string }> {
  return wsRequest(
    'remove_chat_reaction',
    { message_id: messageID, emoji },
    'chat_reaction_update',
    { kind: 'chat_message', id: messageID, action: 'remove_reaction' },
  )
}

// setActiveChatRoom tells the server which chat room this device currently
// has open so it can suppress redundant APNs pushes. Pass null on unmount.
export function setActiveChatRoom(roomID: string | null, threadRootID?: string): void {
  sendMessage(
    { type: 'set_active_chat_room', data: { room_id: roomID ?? '', thread_root_id: threadRootID ?? '' } },
    { kind: 'chat_room', id: roomID ?? '', action: 'set_active' },
  )
}

export function createChatRoom(args: {
  orgID: string
  name: string
  hostID?: string
  memberUserIDs?: string[]
}): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'create_chat_room',
    {
      name: args.name,
      host_id: args.hostID ?? '',
      member_human_user_ids: args.memberUserIDs ?? [],
    },
    'chat_room_created',
    { kind: 'organization', id: args.orgID, action: 'create_chat_room' },
  )
}

export function joinChatRoom(
  roomID: string,
): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'join_chat_room',
    { room_id: roomID },
    'chat_room_joined',
    { kind: 'chat_room', id: roomID, action: 'join' },
  )
}

export function leaveChatRoom(
  roomID: string,
): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'leave_chat_room',
    { room_id: roomID },
    'chat_room_left',
    { kind: 'chat_room', id: roomID, action: 'leave' },
  )
}

export function renameChatRoom(
  roomID: string,
  name: string,
): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'rename_chat_room',
    { room_id: roomID, name },
    'chat_room_renamed',
    { kind: 'chat_room', id: roomID, action: 'rename' },
  )
}

export function setChatRoomHost(
  roomID: string,
  hostID: string,
): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'set_chat_room_host',
    { room_id: roomID, host_id: hostID },
    'chat_room_host_set',
    { kind: 'chat_room', id: roomID, action: 'set_host' },
  )
}

export function deleteChatRoom(
  roomID: string,
): Promise<{ type: string; room_id?: string; error?: string }> {
  return wsRequest(
    'delete_chat_room',
    { room_id: roomID },
    'chat_room_deleted',
    { kind: 'chat_room', id: roomID, action: 'delete' },
  )
}

export function listChatRoomMembers(roomID: string): Promise<{
  type: string
  room_id?: string
  members?: import('../store/types').ChatRoomMember[]
  error?: string
}> {
  return wsRequest(
    'list_chat_room_members',
    { room_id: roomID },
    'chat_room_members_list',
    { kind: 'chat_room', id: roomID, action: 'list_members' },
  )
}
